import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SkeletonBase from 'react-loading-skeleton';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';

import { useReportsDashboard } from './hooks/useReportsDashboard';
import { reportsApi } from './api/reportsApi';
import type { DateRange, CertType, FormTypeUsageResponse, TopAgentsResponse, TopAgentItem, LobBreakdownResponse, LobItem, UserTypeBreakdownResponse, UserTypeBreakdownRow, ErrorSummaryResponse, ErrorsOverTimeResponse } from './types/reports';

import '../../styles/reports.css';

/* ── colour palette ── */
const C = {
  primary: '#A200FF', success: '#16a34a', danger: '#ef4444',
  warning: '#f59e0b', info: '#8500D0', purple: '#8b5cf6', text: '#1e293b', textSec: '#94a3b8',
};

const DATE_FILTERS: { label: string; value: DateRange }[] = [
  { label: 'Today',   value: 'today' },
  { label: '7 Days',  value: '7d'    },
  { label: '30 Days', value: '30d'   },
  { label: 'Custom',  value: 'custom'},
];

/* ── date helpers ── */
const TODAY_STR     = new Date().toISOString().split('T')[0];
const ONE_YEAR_AGO  = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
const LAST_7_DAYS   = new Date(Date.now() -   7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
const LAST_30_DAYS  = new Date(Date.now() -  30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

function fmtDisplayDate(iso: string) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}

/* ─────────────────────────────────────────────
   SKELETON COMPONENTS
───────────────────────────────────────────── */

function SkLine({ w, h = 10 }: { w: string | number; h?: number }) {
  return <SkeletonBase width={w} height={h} />;
}

function KpiSkeleton() {
  return (
    <div className="reports__kpiSkeleton">
      <SkeletonBase width={34} height={34} borderRadius={8} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
        <SkLine w="55%" h={16} /><SkLine w="70%" h={8} /><SkLine w="50%" h={8} />
      </div>
    </div>
  );
}

function AreaSkeleton() {
  return (
    <div className="rpt__skArea">
      <div className="rpt__skAreaLegend">
        {[72, 56, 48].map((w, i) => <SkeletonBase key={i} width={w} height={8} borderRadius={4} />)}
      </div>
      <div className="rpt__skAreaWrap">
        <svg className="rpt__skAreaSvg" viewBox="0 0 500 130" preserveAspectRatio="none">
          <defs>
            <linearGradient id="skFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#dde6f0" stopOpacity="0.9"/>
              <stop offset="100%" stopColor="#dde6f0" stopOpacity="0.1"/>
            </linearGradient>
          </defs>
          <path d="M0,100 C40,95 70,55 110,65 C150,75 180,30 230,25 C270,20 300,50 340,38 C375,27 420,55 460,35 L500,38 L500,130 L0,130 Z"
            fill="url(#skFill)" />
          <path d="M0,100 C40,95 70,55 110,65 C150,75 180,30 230,25 C270,20 300,50 340,38 C375,27 420,55 460,35 L500,38"
            fill="none" stroke="#c8d6e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          {([[0,100],[110,65],[230,25],[340,38],[460,35],[500,38]] as [number,number][]).map(([x,y],i) => (
            <circle key={i} cx={x} cy={y} r="4.5" fill="#b0c4d8" />
          ))}
        </svg>
        <div className="rpt__skAreaXAxis">
          {[0,1,2,3,4,5,6].map(i => <SkeletonBase key={i} width={32} height={7} borderRadius={3} />)}
        </div>
      </div>
    </div>
  );
}

function DonutSkeleton() {
  return (
    <div className="rpt__skDonut">
      <div className="rpt__skDonutRing" />
      <div className="rpt__skDonutLegend">
        {[['62px'], ['52px']].map(([w], i) => (
          <div key={i} className="rpt__skDonutLegendRow">
            <div className="rpt__skDonutDot rpt__skBase" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <SkeletonBase width={w} height={9} borderRadius={4} />
              <SkeletonBase width={36} height={7} borderRadius={3} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TreemapSkeleton() {
  const bars = [88, 72, 65, 58, 50, 44, 40, 36, 30, 26];
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 6, padding: '6px 8px', boxSizing: 'border-box', overflow: 'hidden' }}>
      {bars.map((w, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, height: 20 }}>
          <div className="rpt__skBase" style={{ width: 100, height: 10, borderRadius: 4, flexShrink: 0 }} />
          <div className="rpt__skBase" style={{ width: `${w}%`, height: 2, borderRadius: 1 }} />
          <div className="rpt__skBase" style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0 }} />
        </div>
      ))}
    </div>
  );
}

function TopInsuredsSkeleton({ rows = 10 }: { rows?: number }) {
  const BARS   = [100, 65, 58, 44, 40, 34, 28, 24, 20, 18, 16, 14, 13, 12, 11, 10, 9, 8, 8, 7];
  const NAMES  = [ 78, 62, 70, 52, 66, 58, 74, 48, 60, 55, 68, 50, 64, 56, 72, 46, 62, 54, 70, 52];
  return (
    <div className="rpt__skTopTable">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="rpt__skTopRow">
          <SkeletonBase circle width={20} height={20} />
          <SkeletonBase width={NAMES[i % NAMES.length]} height={10} borderRadius={4} />
          <div className="rpt__skTopBar">
            <SkeletonBase width={`${BARS[i % BARS.length]}%`} height={10} borderRadius={5} />
          </div>
          <SkeletonBase width={30} height={10} borderRadius={4} />
          <SkeletonBase width={26} height={10} borderRadius={4} />
        </div>
      ))}
    </div>
  );
}

function HBarSkeleton({ rows = 5 }: { rows?: number }) {
  const widths = [85, 65, 75, 50, 60, 72, 58, 80];
  return (
    <div className="rpt__skBars">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="rpt__skBarRow">
          <SkLine w={60} h={9} />
          <div style={{ flex: 1 }}>
            <SkeletonBase width={`${widths[i % widths.length]}%`} height={14} borderRadius={3} />
          </div>
        </div>
      ))}
    </div>
  );
}

function VBarSkeleton({ cols = 8 }: { cols?: number }) {
  const heights = [78, 52, 68, 42, 88, 58, 72, 46, 62, 38, 82, 50, 66, 44];
  return (
    <div className="rpt__skVBars">
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} className="rpt__skVBarCol">
          <SkeletonBase width="100%" height={heights[i % heights.length]} borderRadius={4} />
          <SkeletonBase width={30} height={7} borderRadius={2} />
        </div>
      ))}
    </div>
  );
}

function HeatmapSkeleton() {
  return (
    <div className="rpt__skHeatmap">
      {['Sat', 'Fri', 'Thu', 'Wed', 'Tue', 'Mon', 'Sun'].map(d => (
        <div key={d} className="rpt__skHeatRow">
          <SkeletonBase width={22} height={8} borderRadius={2} />
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} style={{ flex: 1, height: 12 }}>
              <SkeletonBase width="100%" height={12} borderRadius={2} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}


/* ─────────────────────────────────────────────
   UTILITY STATES
───────────────────────────────────────────── */

function CardError({ message }: { message: string }) {
  return (
    <div className="reports__stateBox reports__stateBox--error">
      <i className="pi pi-exclamation-circle" />
      <span>{message}</span>
    </div>
  );
}

function EmptyChart({ label = 'No data for this period' }: { label?: string }) {
  return (
    <div className="reports__stateBox reports__stateBox--empty">
      <i className="pi pi-chart-bar" />
      <span>{label}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────
   CHART OPTION BUILDERS
───────────────────────────────────────────── */

const noDataCfg = {
  text: 'No data for this period',
  align: 'center' as const,
  verticalAlign: 'middle' as const,
  style: { fontSize: '11px', color: '#94a3b8' },
};

const base: ApexOptions = {
  chart: {
    fontFamily: 'inherit',
    toolbar:    { show: false },
    background: 'transparent',
    animations: { enabled: true, speed: 400, animateGradually: { enabled: false } },
  },
  grid: { borderColor: '#f1f5f9', strokeDashArray: 4,
    padding: { left: 4, right: 4, top: 0, bottom: 0 } },
  states: { hover: { filter: { type: 'darken' } } },
  noData: noDataCfg,
};

const xAxisStyle = {
  labels: { style: { fontSize: '9px', colors: C.textSec } },
  axisBorder: { show: false },
  axisTicks:  { show: false },
} as const;

const yAxisStyle = {
  labels: { style: { fontSize: '9px', colors: C.textSec } },
} as const;

function areaOpts(categories: string[], seriesData?: number[][]): ApexOptions {
  const total   = categories.length;
  const tickAmt = total <= 7 ? total : total <= 31 ? 8 : 10;

  // Find peak index across all series for annotation
  const allVals  = (seriesData ?? []).flat();
  const maxVal   = allVals.length ? Math.max(...allVals) : 0;
  const peakIdx  = (seriesData?.[0] ?? []).indexOf(maxVal);
  const peakCat  = peakIdx >= 0 ? categories[peakIdx] : null;

  return {
    ...base,
    chart: { ...base.chart, type: 'area' },
    stroke: { curve: 'smooth', width: 2.5, lineCap: 'round' },
    fill: {
      type: 'gradient',
      gradient: {
        type: 'vertical',
        shadeIntensity: 1,
        opacityFrom: 0.28,
        opacityTo: 0.02,
        stops: [0, 90, 100],
      },
    },
    colors: [C.primary, C.danger, C.warning],
    grid: {
      borderColor: '#f1f5f9',
      strokeDashArray: 4,
      padding: { left: 4, right: 8, top: 4, bottom: 4 },
    },
    xaxis: {
      categories,
      tickAmount: tickAmt,
      labels: {
        style: { fontSize: '9px', colors: C.textSec, fontWeight: '500' },
        rotate: -30,
        rotateAlways: false,
        hideOverlappingLabels: true,
        trim: false,
        offsetY: 2,
      },
      axisBorder: { show: false },
      axisTicks:  { show: true, color: '#e2e8f0', height: 3 },
    },
    yaxis: { ...yAxisStyle, tickAmount: 4, min: 0 },
    markers: {
      size: 4,
      strokeWidth: 2,
      strokeColors: '#fff',
      hover: { size: 7, sizeOffset: 2 },
      fillOpacity: 1,
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [0],
      formatter: (val: number) => val > 0 ? String(val) : '',
      style: { fontSize: '9px', fontWeight: '700', colors: [C.primary] },
      offsetY: -7,
      background: {
        enabled: true,
        foreColor: C.primary,
        borderRadius: 4,
        padding: 2,
        borderWidth: 0,
        opacity: 0.08,
        dropShadow: { enabled: false },
      },
    },
    annotations: peakCat ? {
      xaxis: [{
        x: peakCat,
        borderColor: '#f59e0b',
        borderWidth: 1.5,
        strokeDashArray: 3,
        label: {
          text: `Peak · ${maxVal}`,
          style: { color: '#fff', background: '#f59e0b', fontSize: '9px', fontWeight: '700', padding: { left: 5, right: 5, top: 2, bottom: 2 } },
          position: 'top',
          orientation: 'horizontal',
        },
      }],
    } : {},
    tooltip: {
      theme: 'light', shared: true, intersect: false,
      x: { show: true },
      y: { formatter: (v: number) => `${v} cert${v !== 1 ? 's' : ''}` },
      marker: { show: true },
    },
    legend: {
      position: 'top', horizontalAlign: 'right',
      fontSize: '10px', markers: { size: 5 }, offsetY: -4,
      itemMargin: { horizontal: 8 },
    },
  };
}


function hBarOpts(categories: string[], color: string): ApexOptions {
  return {
    ...base,
    chart: { ...base.chart, type: 'bar' },
    plotOptions: { bar: { horizontal: true, barHeight: '60%', borderRadius: 4 } },
    colors: [color],
    xaxis: { ...xAxisStyle, categories, tickAmount: 4 },
    yaxis: {
      labels: {
        style: { fontSize: '9.5px', colors: C.text, fontWeight: '600' },
        maxWidth: 155,
        // trim long names so they never overflow the left gutter
        formatter: (val: number | string) => {
          const s = String(val);
          return s.length > 18 ? s.slice(0, 17) + '…' : s;
        },
      },
    },
    tooltip: {
      theme: 'light',
      y: { formatter: (v: number) => v.toLocaleString() },
      // show full name in tooltip even when truncated
      x: { formatter: (val: string) => val },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: '9px', fontWeight: '700', colors: ['#fff'] },
      dropShadow: { enabled: false },
    },
    grid: { ...base.grid, padding: { left: 6, right: 14, top: -4, bottom: 4 } },
  };
}

function heatmapOpts(): ApexOptions {
  // Show a label every 2 hours: 12am, 2am, 4am … 10pm
  const EVERY_2H = new Set(PEAK_ALL_HOURS.filter((_, i) => i % 2 === 0));
  return {
    ...base,
    chart: { ...base.chart, type: 'heatmap' },
    plotOptions: {
      heatmap: {
        radius: 3,
        enableShades: true,
        shadeIntensity: 0.55,
        colorScale: {
          ranges: [
            { from: 0,   to: 0,   color: '#f0f4f8', name: 'None'   },
            { from: 1,   to: 10,  color: '#e6b3ff', name: 'Low'    },
            { from: 11,  to: 30,  color: '#d580ff', name: 'Medium' },
            { from: 31,  to: 60,  color: '#8500D0', name: 'High'   },
            { from: 61,  to: 999, color: '#381574', name: 'Peak'   },
          ],
        },
      },
    },
    xaxis: {
      type: 'category',
      labels: {
        style:    { fontSize: '7.5px', colors: C.textSec },
        rotate:   0,
        formatter: (val: string) => EVERY_2H.has(val) ? val : '',
      },
      axisBorder: { show: false },
      axisTicks:  { show: false },
    },
    yaxis: { labels: { style: { fontSize: '9px', colors: C.text, fontWeight: '600' } } },
    dataLabels: {
      enabled: true,
      style:   { fontSize: '6.5px', fontWeight: '700', colors: ['#1e3a8a'] },
      formatter: (v: number) => v > 0 ? `${v}` : '',
      background: { enabled: false },
    },
    tooltip: {
      theme: 'light',
      x: { formatter: (val: string) => `${val}` },
      y: { formatter: (v: number) => `${v} cert${v !== 1 ? 's' : ''} sent` },
    },
    legend: { show: true, position: 'top', horizontalAlign: 'right',
      fontSize: '9px', markers: { size: 4 }, offsetY: -4 },
    grid: { ...base.grid, padding: { left: 0, right: 2, top: -8, bottom: 0 } },
  };
}

function radialOpts(labels: string[]): ApexOptions {
  return {
    ...base,
    chart: { ...base.chart, type: 'radialBar' },
    plotOptions: {
      radialBar: {
        offsetY: -5,
        hollow: { size: '20%', background: 'transparent' },
        track:  { background: '#f1f5f9', margin: 2 },
        dataLabels: {
          name:  { fontSize: '8px',  color: C.textSec, offsetY: -2 },
          value: { fontSize: '11px', fontWeight: '700', color: C.text, offsetY: 2,
            formatter: (v: number) => `${v}%` },
        },
      },
    },
    colors: [C.primary, C.success, C.info, C.warning, C.purple],
    labels,
    legend: { show: true, position: 'bottom', fontSize: '10px',
      markers: { size: 4 }, offsetY: 6 },
  };
}

/* Vertical column chart for ACORD form types — distributed colors, rotated labels */
function formTypeColOpts(categories: string[]): ApexOptions {
  // Shorten labels for axis: strip the (2016) edition tag, keep +101 suffix
  // e.g. "ACORD 25 (2016)" → "ACORD 25"   |   "ACORD 25 (2016) + 101" → "ACORD 25+101"
  const shortLabels = categories.map(c =>
    c.replace(' (2016)', '')     // drop edition tag entirely
     .replace(' + 101', '+101') // tighten spacing on +101 suffix
  );
  return {
    ...base,
    chart: { ...base.chart, type: 'bar' },
    plotOptions: {
      bar: {
        horizontal: false, borderRadius: 4, columnWidth: '50%',
        distributed: true,   // each stage gets its own colour = progressive feel
        dataLabels: { position: 'top' },
      },
    },
    colors: ['#f0ddff', '#d580ff', '#8500D0', C.primary],
    xaxis: {
      categories: shortLabels,
      labels: {
        style:    { fontSize: '9px', colors: C.textSec, fontWeight: '500' },
        rotate:   -40,
        rotateAlways: true,
        hideOverlappingLabels: false,
        trim:     false,
        offsetY:  2,
      },
      axisBorder: { show: false },
      axisTicks:  { show: false },
    },
    yaxis: { ...yAxisStyle, tickAmount: 4, min: 0 },
    dataLabels: {
      enabled:  true,
      offsetY:  -14,
      style:    { fontSize: '9px', fontWeight: '700', colors: [C.text] },
      formatter: (v: number) => v > 0 ? v.toLocaleString() : '',
      background: { enabled: false },
    },
    tooltip: {
      theme: 'light',
      x: {
        // Show full label in tooltip (map back from short label to original)
        formatter: (_: string, opts: { dataPointIndex: number }) =>
          categories[opts.dataPointIndex] ?? '',
      },
      y: { formatter: (v: number) => `${v.toLocaleString()} cert${v !== 1 ? 's' : ''}` },
    },
    legend: { show: false },
    grid: {
      borderColor: '#f1f5f9',
      strokeDashArray: 4,
      padding: { left: 4, right: 4, top: 0, bottom: 0 },
    },
  };
}

function userTypeOpts(categories: string[]): ApexOptions {
  return {
    ...base,
    chart: { ...base.chart, type: 'bar' },
    plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 4, distributed: true } },
    colors: [C.primary, C.purple],
    xaxis: { ...xAxisStyle, categories, tickAmount: 4 },
    yaxis: { labels: { style: { fontSize: '10px', colors: C.text, fontWeight: '600' }, maxWidth: 80 } },
    dataLabels: { enabled: true,
      style: { fontSize: '9px', fontWeight: '700', colors: ['#fff'] },
      formatter: (v: number) => v > 0 ? v.toLocaleString() : '',
    },
    tooltip: { theme: 'light', y: { formatter: (v: number) => `${v.toLocaleString()} requests` } },
    legend: { show: false },
  };
}

function errorBarOpts(categories: string[]): ApexOptions {
  const total   = categories.length;
  const tickAmt = total <= 7 ? total : total <= 31 ? 8 : 10;
  return {
    ...base,
    chart: { ...base.chart, type: 'bar', stacked: false },
    plotOptions: { bar: { columnWidth: '60%', borderRadius: 3 } },
    colors: ['#ef4444', '#f97316', '#a855f7', '#94a3b8'],
    xaxis: {
      categories,
      tickAmount: tickAmt,
      labels: {
        style: { fontSize: '9px', colors: C.textSec, fontWeight: '500' },
        rotate: -30, rotateAlways: false, hideOverlappingLabels: true,
      },
      axisBorder: { show: false },
      axisTicks:  { show: true, color: '#e2e8f0', height: 3 },
    },
    yaxis: { ...yAxisStyle, tickAmount: 4, min: 0 },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => val > 0 ? String(val) : '',
      style: { fontSize: '8px', fontWeight: '700' },
      offsetY: -4,
      background: { enabled: false },
    },
    tooltip: {
      theme: 'light', shared: true, intersect: false,
      y: { formatter: (v: number) => `${v} error${v !== 1 ? 's' : ''}` },
    },
    legend: {
      position: 'top', horizontalAlign: 'right',
      fontSize: '10px', markers: { size: 5 }, offsetY: -4,
      itemMargin: { horizontal: 8 },
    },
    grid: { ...base.grid, padding: { left: 4, right: 8, top: 0, bottom: 4 } },
  };
}

/* ── helpers ── */
function trendLabel(change: number) {
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(1)}% vs prior period`;
}

function hasData(arr: number[]) { return arr.some(v => v > 0); }

/* ── COI Volume drill aggregation ── */
type DrillLevel = 'day' | 'week' | 'month';
type RotRaw = { categories: string[]; sent: number[]; failed: number[]; pending: number[] };

function aggregateROT(raw: RotRaw, level: DrillLevel): RotRaw {
  if (level === 'day' || raw.categories.length === 0) return raw;

  function sumChunks(vals: number[], cats: string[]): { cats: string[]; vals: number[] } {
    if (level === 'week') {
      const out: string[] = [], sums: number[] = [];
      for (let i = 0; i < cats.length; i += 7) {
        out.push(cats[i]);
        sums.push(vals.slice(i, i + 7).reduce((a, b) => a + b, 0));
      }
      return { cats: out, vals: sums };
    }
    // month: group by first word of label (e.g. "May" from "May 14")
    const map = new Map<string, number>();
    const order: string[] = [];
    cats.forEach((c, i) => {
      const key = c.includes(':') ? c : c.split(' ')[0]; // guard for "HH:mm" labels
      if (!map.has(key)) { map.set(key, 0); order.push(key); }
      map.set(key, (map.get(key) ?? 0) + (vals[i] ?? 0));
    });
    return { cats: order, vals: order.map(k => map.get(k) ?? 0) };
  }

  const { cats, vals: sent }    = sumChunks(raw.sent,    raw.categories);
  const { vals: failed }        = sumChunks(raw.failed,  raw.categories);
  const { vals: pending }       = sumChunks(raw.pending, raw.categories);
  return { categories: cats, sent, failed, pending };
}

/* ── CSV download — COI Volume ── */
function downloadRotCSV(rotData: RotRaw, filename: string) {
  const showFailed  = hasData(rotData.failed);
  const showPending = hasData(rotData.pending);
  const header = ['Date', 'Sent', ...(showFailed ? ['Failed'] : []), ...(showPending ? ['Pending'] : [])].join(',');
  const rows = rotData.categories.map((c, i) => [
    c,
    rotData.sent[i] ?? 0,
    ...(showFailed  ? [rotData.failed[i]  ?? 0] : []),
    ...(showPending ? [rotData.pending[i] ?? 0] : []),
  ].join(','));
  const csv  = [header, ...rows].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

/* ── CSV download — ACORD Form Types ── */
function downloadFormTypeCSV(categories: string[], values: number[], filename: string) {
  const csv = ['Form,Count', ...categories.map((c, i) => `${c},${values[i] ?? 0}`)].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}


function downloadLobCSV(lobs: LobItem[], total: number, filename: string) {
  const rows = [
    ['Rank', 'Line of Business', 'Certificates', '% Share'],
    ...lobs.map((l, i) => [
      String(i + 1),
      `"${l.lob.replace(/"/g, '""')}"`,
      String(l.count),
      total > 0 ? (l.count / total * 100).toFixed(1) + '%' : '0%',
    ]),
  ];
  const csv  = rows.map(r => r.join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function downloadTopInsuredsCSV(
  agents: { name: string; count: number }[],
  total: number,
  filename: string,
) {
  const rows = [
    ['Rank', 'Insured Name', 'COI Count', '% Share'],
    ...agents.map((a, i) => [
      String(i + 1),
      `"${a.name.replace(/"/g, '""')}"`,
      String(a.count),
      total > 0 ? (a.count / total * 100).toFixed(1) + '%' : '0%',
    ]),
  ];
  const csv  = rows.map(r => r.join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

/* ── Peak Usage helpers ── */
function peakHourLabel(i: number): string {
  if (i === 0)  return '12am';
  if (i < 12)   return `${i}am`;
  if (i === 12) return '12pm';
  return `${i - 12}pm`;
}
const PEAK_ALL_HOURS   = Array.from({ length: 24 }, (_, i) => peakHourLabel(i));
const PEAK_BIZ_HOURS   = PEAK_ALL_HOURS.slice(8, 18);  // 8am–5pm
const PEAK_AFTER_HOURS = [...PEAK_ALL_HOURS.slice(0, 8), ...PEAK_ALL_HOURS.slice(18)];

function peakCellStyle(v: number): React.CSSProperties {
  if (v === 0)  return { background: '#f0f4f8', color: '#c4cdd6' };
  if (v <= 2)   return { background: '#f3e8ff', color: '#7c3aed' };
  if (v <= 5)   return { background: '#e9d5ff', color: '#6d28d9' };
  if (v <= 10)  return { background: '#c084fc', color: '#3b0764' };
  if (v <= 20)  return { background: '#a855f7', color: '#fff'    };
  if (v <= 40)  return { background: '#8500D0', color: '#fff'    };
  return         { background: '#381574', color: '#f3e8ff'   };
}

/* ─────────────────────────────────────────────
   PAGE
───────────────────────────────────────────── */

export default function ReportsPage() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>('7d');
  const [customFrom, setCustomFrom] = useState(LAST_7_DAYS);
  const [customTo,   setCustomTo]   = useState(TODAY_STR);
  const fromRef = useRef<HTMLInputElement>(null);
  const toRef   = useRef<HTMLInputElement>(null);

  /* ── Global view mode: overrides all card views when set ── */
  const [globalView, setGlobalView] = useState<'chart' | 'table' | null>(null);
  const [xlsLoading, setXlsLoading] = useState(false);

  function effectiveView(local: 'chart' | 'table'): 'chart' | 'table' {
    return globalView ?? local;
  }

  async function handleExportExcel() {
    if (xlsLoading) return;
    setXlsLoading(true);
    try {
      const base  = process.env.REACT_APP_COIPortal_Url ?? '';
      const token = localStorage.getItem('igtoken') ?? '';
      const nc    = localStorage.getItem('NCToken') ?? '';
      const dr    = dateRange;
      const df    = dateRange === 'custom' ? customFrom : '';
      const dt    = dateRange === 'custom' ? customTo   : '';
      const qs    = new URLSearchParams({ dateRange: dr, ...(df ? { dateFrom: df } : {}), ...(dt ? { dateTo: dt } : {}) });
      const res   = await fetch(`${base}/api/igigcoi/reports/export-excel?${qs}`, {
        headers: { Authorization: `Bearer ${token}`, NCToken: nc },
      });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob     = await res.blob();
      const url      = URL.createObjectURL(blob);
      const a        = document.createElement('a');
      a.href         = url;
      a.download     = `COI-Reports-${dr}-${TODAY_STR}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setXlsLoading(false);
    }
  }

  /* COI Volume chart controls */
  const [rotView,   setRotView]   = useState<'chart' | 'table'>('chart');
  const [rotDrill,  setRotDrill]  = useState<DrillLevel>('day');
  const [rotSearch, setRotSearch] = useState('');

  /* Refresh tick — incremented on manual refresh to re-trigger independent card effects */
  const [refreshTick,   setRefreshTick]   = useState(0);
  const [isRefreshing,  setIsRefreshing]  = useState(false);

  /* ACORD Form Types — independent date range + local fetch */
  const [formView,      setFormView]      = useState<'chart' | 'table'>('chart');
  const [formSearch,    setFormSearch]    = useState('');
  const [formDateRange, setFormDateRange] = useState<DateRange>('7d');
  const [formApiData,   setFormApiData]   = useState<FormTypeUsageResponse | null>(null);
  const [formLoading,   setFormLoading]   = useState(true);
  const [formError,     setFormError]     = useState<string | null>(null);

  /* Peak Usage controls */
  const [peakView,   setPeakView]   = useState<'chart' | 'table'>('chart');
  const [peakFilter, setPeakFilter] = useState<'all' | 'business' | 'after'>('all');

  /* Top Insureds — independent date range + local fetch */
  const [topView,      setTopView]      = useState<'chart' | 'table'>('chart');
  const [topSearch,    setTopSearch]    = useState('');
  const [topLimit,     setTopLimit]     = useState<5 | 10 | 20>(10);
  const [topDateRange, setTopDateRange] = useState<DateRange>('7d');
  const [topApiData,   setTopApiData]   = useState<TopAgentsResponse | null>(null);
  const [topLoading,   setTopLoading]   = useState(true);
  const [topError,     setTopError]     = useState<string | null>(null);

  /* Certificates by LOB — independent date range + local fetch */
  const [lobView,      setLobView]      = useState<'chart' | 'table'>('chart');
  const [lobSearch,    setLobSearch]    = useState('');
  const [lobDateRange, setLobDateRange] = useState<DateRange>('7d');
  const [lobApiData,   setLobApiData]   = useState<LobBreakdownResponse | null>(null);
  const [lobLoading,   setLobLoading]   = useState(true);
  const [lobError,     setLobError]     = useState<string | null>(null);

  /* Agent vs Internal — independent date range, certType toggle, local fetch */
  const [agentView,      setAgentView]      = useState<'chart' | 'table'>('chart');
  const [agentCertType,  setAgentCertType]  = useState<CertType>('sent');
  const [agentDateRange, setAgentDateRange] = useState<DateRange>('7d');
  const [agentApiData,   setAgentApiData]   = useState<UserTypeBreakdownResponse | null>(null);
  const [agentLoading,   setAgentLoading]   = useState(true);
  const [agentError,     setAgentError]     = useState<string | null>(null);

  /* Error Reports — independent date range + local fetch */
  const [errView,      setErrView]      = useState<'chart' | 'table'>('chart');
  const [errCollapsed, setErrCollapsed] = useState(true);
  const errCardRef = useRef<HTMLDivElement>(null);
  const [errDateRange, setErrDateRange] = useState<DateRange>('7d');
  const [errSummary,   setErrSummary]   = useState<ErrorSummaryResponse | null>(null);
  const [errApiData,   setErrApiData]   = useState<ErrorsOverTimeResponse | null>(null);
  const [errLoading,   setErrLoading]   = useState(true);
  const [errError,     setErrError]     = useState<string | null>(null);

  /* Reset drill level when date range changes to shorter periods */
  const prevDateRange = useRef(dateRange);
  if (prevDateRange.current !== dateRange) {
    prevDateRange.current = dateRange;
    if (dateRange === 'today' && rotDrill !== 'day') setRotDrill('day');
    if (dateRange === '7d'    && rotDrill === 'month') setRotDrill('day');
  }

  /* Sync global date range → all independent cards */
  useEffect(() => {
    setFormDateRange(dateRange);
    setTopDateRange(dateRange);
    setLobDateRange(dateRange);
    setAgentDateRange(dateRange);
    setErrDateRange(dateRange);
  }, [dateRange]);

  /* Independent fetch for ACORD Form Types (respects custom global dates) */
  useEffect(() => {
    const controller = new AbortController();
    setFormLoading(true);
    setFormError(null);
    const df = formDateRange === 'custom' ? customFrom : undefined;
    const dt = formDateRange === 'custom' ? customTo   : undefined;
    reportsApi.getFormTypeUsage(formDateRange, controller.signal, df, dt)
      .then(d => { setFormApiData(d); setFormLoading(false); })
      .catch(e => { if (!controller.signal.aborted) { setFormError(e.message); setFormLoading(false); } });
    return () => controller.abort();
  }, [formDateRange, customFrom, customTo, refreshTick]);

  /* Independent fetch for Certificates by LOB */
  useEffect(() => {
    const controller = new AbortController();
    setLobLoading(true);
    setLobError(null);
    const df = lobDateRange === 'custom' ? customFrom : undefined;
    const dt = lobDateRange === 'custom' ? customTo   : undefined;
    reportsApi.getLobBreakdown(lobDateRange, controller.signal, df, dt)
      .then(d => { setLobApiData(d); setLobLoading(false); })
      .catch(e => { if (!controller.signal.aborted) { setLobError(e.message); setLobLoading(false); } });
    return () => controller.abort();
  }, [lobDateRange, customFrom, customTo, refreshTick]);

  /* Independent fetch for Agent vs Internal Usage */
  useEffect(() => {
    const controller = new AbortController();
    setAgentLoading(true);
    setAgentError(null);
    const df = agentDateRange === 'custom' ? customFrom : undefined;
    const dt = agentDateRange === 'custom' ? customTo   : undefined;
    reportsApi.getUserTypeBreakdown(agentCertType, agentDateRange, controller.signal, df, dt)
      .then(d  => { setAgentApiData(d); setAgentLoading(false); })
      .catch(e => { if (!controller.signal.aborted) { setAgentError(e.message); setAgentLoading(false); } });
    return () => controller.abort();
  }, [agentDateRange, agentCertType, customFrom, customTo, refreshTick]);

  /* Independent fetch for Top Insureds (respects custom global dates) */
  useEffect(() => {
    const controller = new AbortController();
    setTopLoading(true);
    setTopError(null);
    const df = topDateRange === 'custom' ? customFrom : undefined;
    const dt = topDateRange === 'custom' ? customTo   : undefined;
    reportsApi.getTopAgents(topDateRange, controller.signal, df, dt, topLimit)
      .then(d => { setTopApiData(d); setTopLoading(false); })
      .catch(e => { if (!controller.signal.aborted) { setTopError(e.message); setTopLoading(false); } });
    return () => controller.abort();
  }, [topDateRange, customFrom, customTo, topLimit, refreshTick]);

  /* Independent fetch for Error Reports (summary + over-time in parallel) */
  useEffect(() => {
    const controller = new AbortController();
    setErrLoading(true);
    setErrError(null);
    const df = errDateRange === 'custom' ? customFrom : undefined;
    const dt = errDateRange === 'custom' ? customTo   : undefined;
    Promise.all([
      reportsApi.getErrorSummary(errDateRange, controller.signal, df, dt),
      reportsApi.getErrorsOverTime(errDateRange, controller.signal, df, dt),
    ])
      .then(([summary, overTime]) => {
        setErrSummary(summary);
        setErrApiData(overTime);
        setErrLoading(false);
      })
      .catch(e => {
        if (!controller.signal.aborted) { setErrError(e.message); setErrLoading(false); }
      });
    return () => controller.abort();
  }, [errDateRange, customFrom, customTo, refreshTick]);

  /* Auto-collapse Error Reports when clicking outside */
  useEffect(() => {
    if (errCollapsed) return;
    function onClickOutside(e: MouseEvent) {
      if (errCardRef.current && !errCardRef.current.contains(e.target as Node)) {
        setErrCollapsed(true);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [errCollapsed]);

  /* Auto-populate last 7 days whenever Custom is selected */
  function handleDateFilter(value: DateRange) {
    setDateRange(value);
    if (value === 'custom') {
      setCustomFrom(LAST_7_DAYS);
      setCustomTo(TODAY_STR);
    }
  }

  /* Compute display dates for preset ranges */
  const displayFrom = dateRange === 'custom' ? customFrom
    : dateRange === 'today' ? TODAY_STR
    : dateRange === '7d'    ? LAST_7_DAYS
    : LAST_30_DAYS;
  const displayTo = dateRange === 'custom' ? customTo : TODAY_STR;

  /* Click on date slot when not in custom mode → switch to custom */
  function handleFromSlotClick() {
    if (dateRange !== 'custom') { handleDateFilter('custom'); }
    else { fromRef.current?.showPicker(); }
  }
  function handleToSlotClick() {
    if (dateRange !== 'custom') { handleDateFilter('custom'); }
    else { toRef.current?.showPicker(); }
  }

  const { data, loading, errors, refetch } = useReportsDashboard(
    dateRange,
    dateRange === 'custom' ? customFrom : undefined,
    dateRange === 'custom' ? customTo   : undefined,
  );

  function handleRefresh() {
    refetch();
    setRefreshTick(t => t + 1);
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 1200);
  }

  /* ── KPI cards — each tied to a chart section ── */
  const kpiCards = useMemo(() => {
    const s  = data.summary;
    const ft = data.formTypeUsage;
    const pu = data.peakUsage;
    const ta = data.topAgents;

    // Peak day: find specific date with highest sent count from requestsOverTime
    const rot = data.requestsOverTime;
    let peakDayName = '—', peakDayDate = '', peakDayCount = 0, peakDayShort = '';
    if (rot && rot.categories.length > 0 && rot.sent.some(v => v > 0)) {
      const maxVal = Math.max(...rot.sent);
      const maxIdx = rot.sent.indexOf(maxVal);
      const label  = rot.categories[maxIdx] ?? '';  // "May 22"
      peakDayCount = maxVal;
      // Parse the date
      const parsed = new Date(`${label} ${new Date().getFullYear()}`);
      if (!isNaN(parsed.getTime())) {
        // Ordinal suffix: 1st, 2nd, 3rd, 4th …
        const d   = parsed.getDate();
        const sfx = d === 1 || d === 21 || d === 31 ? 'st'
                  : d === 2 || d === 22             ? 'nd'
                  : d === 3 || d === 23             ? 'rd' : 'th';
        const month = parsed.toLocaleDateString('en-US', { month: 'long' });
        const year  = parsed.getFullYear();
        // "23rd April 2026"
        peakDayDate  = `${d}${sfx} ${month} ${year}`;
        // "Thursday" — full weekday for sub-label
        peakDayName  = parsed.toLocaleDateString('en-US', { weekday: 'long' });
        // "Thu" — short weekday
        peakDayShort = parsed.toLocaleDateString('en-US', { weekday: 'short' });
      } else {
        peakDayDate = label;
      }
    }

    // Top ACORD form (strip description after " – ")
    const topForm      = ft?.formTypes[0];
    const topFormLabel = topForm ? topForm.name.split(' – ')[0] : '—';

    // Top insured (truncate long names)
    const topInsured      = ta?.agents[0];
    const topInsuredLabel = topInsured
      ? (topInsured.name.length > 15 ? topInsured.name.slice(0, 13) + '…' : topInsured.name)
      : '—';

    return [
      // 1. Total COI — total count of sent certificates (HistoricalCertificateHolderList)
      {
        icon: 'pi pi-send', color: C.primary,
        value: s ? s.certsSent.toLocaleString() : '—',
        label: 'Total COI',
        sub: s ? trendLabel(s.certsSentChange) : '',
        trendPct: s?.certsSentChange,
        loading: loading.summary,
      },
      // 2. Total Master Certs — ServiceRequestCertificateOfInsuranceList (master/source records)
      {
        icon: 'pi pi-file', color: C.info,
        value: s ? s.totalRequests.toLocaleString() : '—',
        label: 'Total Master Certs',
        sub: s ? trendLabel(s.totalRequestsChange) : '',
        trendPct: s?.totalRequestsChange,
        loading: loading.summary,
      },
      // 3. Top ACORD Form — ties to ACORD Form Types chart
      {
        icon: 'pi pi-file-edit', color: C.purple,
        value: topFormLabel,
        label: 'Top ACORD Form',
        sub: topForm ? `${topForm.count.toLocaleString()} certs` : 'No data',
        trendPct: undefined,
        loading: loading.formTypeUsage,
      },
      // 4. Peak Request Day — actual date shown as main value
      {
        icon: 'pi pi-clock', color: C.warning,
        value: peakDayDate || '—',
        label: 'Peak Request Day',
        sub: peakDayCount > 0
          ? `${peakDayName} · ${peakDayCount.toLocaleString()} certs`
          : 'No data',
        trendPct: undefined,
        loading: loading.requestsOverTime,
      },
      // 5. Top Insured — ties to Top Insureds bar chart
      {
        icon: 'pi pi-building', color: C.success,
        value: topInsuredLabel,
        label: 'Top Insured',
        sub: topInsured ? `${topInsured.count.toLocaleString()} COIs` : 'No data',
        trendPct: undefined,
        loading: loading.topAgents,
      },
    ];
  }, [data.summary, data.formTypeUsage, data.requestsOverTime, data.topAgents,
      loading.summary, loading.formTypeUsage, loading.requestsOverTime, loading.topAgents]);

  /* ── COI Volume Over Time (drill-aware) ── */
  const rotData = useMemo(() => {
    const d = data.requestsOverTime;
    if (!d) return null;
    return aggregateROT(d, rotDrill);
  }, [data.requestsOverTime, rotDrill]);

  const rotSeries = useMemo(() => {
    if (!rotData) return [];
    return [
      { name: 'Sent',    data: rotData.sent    },
      { name: 'Failed',  data: rotData.failed  },
      { name: 'Pending', data: rotData.pending },
    ].filter(s => hasData(s.data));
  }, [rotData]);

  /* ── COI Volume summary (today / this week / this month) derived from daily data ── */
  const rotSummary = useMemo(() => {
    // Only meaningful on day drill level and when we have raw daily data
    const raw = data.requestsOverTime;
    if (!raw || raw.categories.length === 0) return null;

    const now   = new Date();
    const year  = now.getFullYear();

    // Short labels the API uses: "May 22"
    const todayShort = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const monthShort = now.toLocaleDateString('en-US', { month: 'short' }); // "May"

    // Monday of current ISO week
    const dow = now.getDay(); // 0=Sun
    const msSinceMon = ((dow === 0 ? 6 : dow - 1)) * 86400000;
    const weekStart  = new Date(now.getTime() - msSinceMon);
    weekStart.setHours(0, 0, 0, 0);

    let todaySent = 0, weekSent = 0, monthSent = 0;

    raw.categories.forEach((cat, i) => {
      const sent    = raw.sent[i] ?? 0;
      const parsed  = new Date(`${cat} ${year}`);
      const isValid = !isNaN(parsed.getTime());

      if (cat === todayShort) todaySent += sent;
      if (cat.startsWith(monthShort + ' ')) monthSent += sent;
      if (isValid && parsed >= weekStart && parsed <= now) weekSent += sent;
    });

    const totalSent = raw.sent.reduce((a, b) => a + b, 0);
    const days      = raw.categories.length || 1;
    const avgPerDay = Math.round(totalSent / days);

    return { todaySent, weekSent, monthSent, totalSent, avgPerDay };
  }, [data.requestsOverTime]);

  /* ── Delivery Breakdown ── */
  const deliverySeries = useMemo(() => {
    const d = data.deliveryBreakdown;
    if (!d) return [];
    return [d.delivered, d.failed, d.pending];
  }, [data.deliveryBreakdown]);

  /* ── ACORD Form Types — uses independent formApiData (own date range) ── */
  const formTypeSeries = useMemo(() => {
    const d = formApiData;
    if (!d) return { categories: [] as string[], values: [] as number[] };
    return {
      categories: d.formTypes.map(t => t.name.split(' – ')[0].trim()),
      values:     d.formTypes.map(t => t.count),
    };
  }, [formApiData]);

  /* ── ACORD Form Types summary strip ── */
  const formSummary = useMemo(() => {
    if (!formApiData || formTypeSeries.values.length === 0) return null;

    const totalCerts  = formTypeSeries.values.reduce((a, b) => a + b, 0);
    const formsUsed   = formTypeSeries.categories.filter((_, i) => formTypeSeries.values[i] > 0).length;
    const topIdx      = formTypeSeries.values.indexOf(Math.max(...formTypeSeries.values));
    const topForm     = formTypeSeries.categories[topIdx] ?? '—';
    const topCount    = formTypeSeries.values[topIdx] ?? 0;

    // Certs that include ACORD 101 (either as ACORD 101 alone or + combo like ACORD 24+101)
    const acord101Count = formTypeSeries.categories.reduce((sum, cat, i) =>
      cat.includes('101') ? sum + (formTypeSeries.values[i] ?? 0) : sum, 0);

    return { totalCerts, formsUsed, topForm, topCount, acord101Count };
  }, [formApiData, formTypeSeries]);

  /* ── Peak Usage — filtered series ── */
  const heatmapSeries = useMemo(() => {
    const d = data.peakUsage;
    if (!d) return [];
    const allowed = peakFilter === 'business' ? new Set(PEAK_BIZ_HOURS)
                  : peakFilter === 'after'    ? new Set(PEAK_AFTER_HOURS)
                  : null;
    return d.days.map(day => ({
      name: day.day,
      data: day.data
        .filter(h => !allowed || allowed.has(h.hour))
        .map(h => ({ x: h.hour, y: h.count })),
    }));
  }, [data.peakUsage, peakFilter]);

  /* ── Peak Usage — summary strip ── */
  const peakSummary = useMemo(() => {
    const d = data.peakUsage;
    if (!d) return null;
    const DAY_FULL: Record<string, string> = {
      Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday',
      Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday',
    };
    let totalCerts = 0;
    let busiestDay = '—'; let busiestDayCount = 0;
    const hourTotals: Record<string, number> = {};

    d.days.forEach(day => {
      const dt = day.data.reduce((a, h) => a + h.count, 0);
      totalCerts += dt;
      if (dt > busiestDayCount) { busiestDayCount = dt; busiestDay = day.day; }
      day.data.forEach(h => { hourTotals[h.hour] = (hourTotals[h.hour] ?? 0) + h.count; });
    });

    let busiestHour = '—'; let busiestHourCount = 0;
    Object.entries(hourTotals).forEach(([hr, cnt]) => {
      if (cnt > busiestHourCount) { busiestHourCount = cnt; busiestHour = hr; }
    });

    // Busiest time period (Morning / Afternoon / Evening / Night)
    const periods = [
      { label: 'Morning',   idxs: [6,7,8,9,10,11] },
      { label: 'Afternoon', idxs: [12,13,14,15,16,17] },
      { label: 'Evening',   idxs: [18,19,20,21,22,23] },
      { label: 'Night',     idxs: [0,1,2,3,4,5] },
    ];
    let busiestPeriod = '—'; let busiestPeriodCnt = 0;
    periods.forEach(p => {
      const cnt = p.idxs.reduce((a, i) => a + (hourTotals[peakHourLabel(i)] ?? 0), 0);
      if (cnt > busiestPeriodCnt) { busiestPeriodCnt = cnt; busiestPeriod = p.label; }
    });

    return {
      totalCerts,
      busiestDay:      DAY_FULL[busiestDay] ?? busiestDay,
      busiestDayCount,
      busiestHour,
      busiestHourCount,
      busiestPeriod,
      busiestPeriodCnt,
    };
  }, [data.peakUsage]);

  /* ── Top Insureds — independent data ── */
  const topAgentsSeries = useMemo(() => {
    if (!topApiData) return { categories: [] as string[], values: [] as number[] };
    const q = topSearch.trim().toLowerCase();
    const agents: TopAgentItem[] = q
      ? topApiData.agents.filter((a: TopAgentItem) => a.name.toLowerCase().includes(q))
      : topApiData.agents;
    return {
      categories: agents.map((a: TopAgentItem) => a.name),
      values:     agents.map((a: TopAgentItem) => a.count),
    };
  }, [topApiData, topSearch]);

  const topSummary = useMemo(() => {
    if (!topApiData) return null;
    const q = topSearch.trim().toLowerCase();
    const agents: TopAgentItem[] = q
      ? topApiData.agents.filter((a: TopAgentItem) => a.name.toLowerCase().includes(q))
      : topApiData.agents;
    const total  = agents.reduce((s: number, a: TopAgentItem) => s + a.count, 0);
    const top1   = agents[0];
    return {
      totalCOIs:    total,
      insuredCount: agents.length,
      topName:      top1?.name  ?? '—',
      topCount:     top1?.count ?? 0,
      topShare:     total > 0 && top1 ? Math.round((top1.count / total) * 100) : 0,
      avgPerInsured: agents.length > 0 ? Math.round(total / agents.length) : 0,
      agents,
    };
  }, [topApiData, topSearch]);

  /* ── Status Distribution ── */
  const statusSeries = useMemo(() => {
    const d = data.statusDistribution;
    if (!d) return { labels: [] as string[], series: [] as number[] };
    return {
      labels: d.industries.map(i => i.industry),
      series: d.industries.map(i => Math.round(i.rate)),
    };
  }, [data.statusDistribution]);

  /* ── Certificates by LOB ── */
  const LOB_COLORS = [
    '#3b82f6','#f97316','#22c55e','#ef4444','#8b5cf6',
    '#14b8a6','#eab308','#ec4899','#6366f1','#84cc16',
    '#0ea5e9','#a855f7','#10b981','#f43f5e','#d97706',
  ];

  const lobFiltered = useMemo((): LobItem[] => {
    if (!lobApiData) return [];
    const q = lobSearch.trim().toLowerCase();
    return q
      ? lobApiData.lobs.filter((l: LobItem) => l.lob.toLowerCase().includes(q))
      : lobApiData.lobs;
  }, [lobApiData, lobSearch]);

  const lobSummary = useMemo(() => {
    if (!lobApiData || lobFiltered.length === 0) return null;
    const total = lobFiltered.reduce((s: number, l: LobItem) => s + l.count, 0);
    const top1  = lobFiltered[0];
    return {
      total,
      lobCount:   lobFiltered.length,
      topLob:     top1?.lob   ?? '—',
      topCount:   top1?.count ?? 0,
      topShare:   total > 0 && top1 ? Math.round((top1.count / total) * 100) : 0,
    };
  }, [lobApiData, lobFiltered]);

  /* ── Error Reports — series + summary strip ── */
  const errSeries = useMemo(() => {
    if (!errApiData) return [];
    return [
      { name: 'Login',            data: errApiData.login      },
      { name: 'Send Certificate', data: errApiData.sendCert   },
      { name: 'Master Cert',      data: errApiData.masterCert },
      { name: 'Other',            data: errApiData.other      },
    ].filter(s => s.data.some(v => v > 0));
  }, [errApiData]);

  const errKpis = useMemo(() => {
    if (!errSummary) return null;
    return [
      { label: 'Total Errors',    value: errSummary.total,            change: errSummary.totalChange,       color: '#ef4444' },
      { label: 'Login',           value: errSummary.loginErrors,      change: errSummary.loginChange,       color: '#f97316' },
      { label: 'Send Certificate',value: errSummary.sendCertErrors,   change: errSummary.sendCertChange,    color: '#a855f7' },
      { label: 'Master Cert',     value: errSummary.masterCertErrors, change: errSummary.masterCertChange,  color: '#6366f1' },
      { label: 'Other',           value: errSummary.otherErrors,      change: 0,                            color: '#94a3b8' },
    ];
  }, [errSummary]);

  const ERROR_TYPE_LABELS: Record<string, { label: string; color: string }> = {
    login:      { label: 'Login',         color: '#ef4444' },
    sendCert:   { label: 'Send Cert',     color: '#f97316' },
    masterCert: { label: 'Master Cert',   color: '#a855f7' },
    other:      { label: 'Other',         color: '#94a3b8' },
  };

  /* ── Agent vs Internal — download helper ── */
  function downloadAgentCSV() {
    if (!agentApiData) return;
    const label = agentCertType === 'sent' ? 'Sent Certificates' : 'Master Certificates';
    const rows  = [
      ['User Type', 'Count', 'Total', 'Share %'],
      ...agentApiData.rows.map((r: UserTypeBreakdownRow) =>
        [r.userType, r.count, r.total, r.sharePct]),
    ];
    const csv  = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `agent-vs-internal-${agentCertType}-${agentDateRange}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const customReady = dateRange !== 'custom' || (!!customFrom && !!customTo);

  return (
    <div className="reports__page">

      {/* ── Header ── */}
      <div className="reports__header">
        <div className="reports__headerLeft">
          <h1>Reports Dashboard</h1>
          <p>COI volume, delivery metrics and usage insights</p>
        </div>
        <div className="reports__headerActions">

          {/* Back to Dashboard — leftmost */}
          <button className="reports__backBtn" onClick={() => navigate('/agentdashboard')}>
            <i className="pi pi-arrow-left" style={{ fontSize: 10 }} />
            Back to Dashboard
          </button>

          <div className="reports__headerDivider" />

          {/* Date filter buttons */}
          <div className="reports__dateFilter">
            {DATE_FILTERS.map(f => (
              <button key={f.value}
                className={`reports__dateBtn${dateRange === f.value ? ' reports__dateBtn--active' : ''}`}
                onClick={() => handleDateFilter(f.value)}
              >{f.label}</button>
            ))}
          </div>

          {/* Date range picker — always visible */}
          <div className="reports__customDatePicker">

            {/* FROM slot */}
            <div
              className={`reports__cdpSlot${dateRange !== 'custom' ? ' reports__cdpSlot--readonly' : ''}`}
              onClick={handleFromSlotClick}
            >
              <div className="reports__cdpSlotContent">
                <span className="reports__cdpSlotLabel">FROM</span>
                <span className="reports__cdpSlotValue">{fmtDisplayDate(displayFrom)}</span>
              </div>
              <div className="reports__cdpCalBtn">
                <i className="pi pi-calendar" />
              </div>
              {dateRange === 'custom' && (
                <input ref={fromRef} type="date" className="reports__cdpInput"
                  min={ONE_YEAR_AGO} max={customTo || TODAY_STR}
                  value={customFrom}
                  onChange={e => setCustomFrom(e.target.value)} />
              )}
            </div>

            <span className="reports__cdpArrow"><i className="pi pi-arrow-right" /></span>

            {/* TO slot */}
            <div
              className={`reports__cdpSlot${dateRange !== 'custom' ? ' reports__cdpSlot--readonly' : ''}`}
              onClick={handleToSlotClick}
            >
              <div className="reports__cdpSlotContent">
                <span className="reports__cdpSlotLabel">TO</span>
                <span className="reports__cdpSlotValue">{fmtDisplayDate(displayTo)}</span>
              </div>
              <div className="reports__cdpCalBtn">
                <i className="pi pi-calendar" />
              </div>
              {dateRange === 'custom' && (
                <input ref={toRef} type="date" className="reports__cdpInput"
                  min={customFrom || ONE_YEAR_AGO} max={TODAY_STR}
                  value={customTo}
                  onChange={e => setCustomTo(e.target.value)} />
              )}
            </div>

          </div>

          <div className="reports__headerDivider" />

          {/* Global chart / table toggle */}
          <div className="reports__globalViewToggle">
            <button
              className={`reports__globalViewBtn${effectiveView('chart') === 'chart' && globalView !== 'table' ? ' reports__globalViewBtn--active' : ''}`}
              onClick={() => setGlobalView(globalView === 'chart' ? null : 'chart')}
              title="All charts view">
              <i className="pi pi-chart-line" /> Charts
            </button>
            <button
              className={`reports__globalViewBtn${globalView === 'table' ? ' reports__globalViewBtn--active' : ''}`}
              onClick={() => setGlobalView(globalView === 'table' ? null : 'table')}
              title="All tables view">
              <i className="pi pi-table" /> Table
            </button>
          </div>

          {/* Export Excel */}
          <button
            className={`reports__xlsBtn${xlsLoading ? ' reports__xlsBtn--loading' : ''}`}
            onClick={handleExportExcel}
            disabled={xlsLoading}
            title={xlsLoading ? 'Generating Excel…' : 'Export all data to Excel'}>
            {xlsLoading
              ? <><span className="reports__xlsSpinner" /><span>Generating…</span></>
              : <><i className="pi pi-file-excel" /><span>Export Excel</span></>}
          </button>

          <button className="reports__iconBtn" onClick={handleRefresh} title="Refresh all charts">
            <i className={`pi pi-refresh${isRefreshing ? ' reports__iconBtn--spinning' : ''}`} />
          </button>

        </div>
      </div>

      {/* ── KPI Strip ── */}
      <div className="reports__kpiStrip">
        {kpiCards.map((kpi, i) =>
          kpi.loading
            ? <KpiSkeleton key={i} />
            : (
              <div key={i} className="reports__kpiCard">
                <div className="reports__kpiIcon"
                  style={{ background: `${kpi.color}18`, color: kpi.color }}>
                  <i className={kpi.icon} />
                </div>
                <div className="reports__kpiBody">
                  <div className="reports__kpiValue">{kpi.value}</div>
                  <div className="reports__kpiLabel">{kpi.label}</div>
                  {kpi.trendPct !== undefined
                    ? (
                      <div className={`reports__kpiTrend reports__kpiTrend--${kpi.trendPct >= 0 ? 'up' : 'down'}`}>
                        <i className={`pi pi-arrow-${kpi.trendPct >= 0 ? 'up' : 'down'}`} style={{ fontSize: 8 }} />
                        {kpi.sub}
                      </div>
                    )
                    : <div className="reports__kpiSub">{kpi.sub}</div>
                  }
                </div>
              </div>
            )
        )}
      </div>

      {/* ── Custom date not set yet (edge case guard) ── */}
      {dateRange === 'custom' && !customReady && (
        <div className="reports__customPrompt">
          <i className="pi pi-calendar" />
          Select a From and To date above to load the report
        </div>
      )}

      {/* ── Chart Grid ── */}
      {customReady && (
        <div className="reports__gridWrap">
        <div className="reports__grid">

          {/* Row 1 col 1 ── COI Volume Over Time */}
          <div className="reports__card">
            <div className="reports__cardHeader">
              <i className="pi pi-chart-line reports__cardIcon" />
              <span className="reports__cardTitle">COI Volume Over Time</span>
              {rotData && !loading.requestsOverTime && (
                <span className="reports__cardBadge">
                  {rotData.sent.reduce((a, b) => a + b, 0).toLocaleString()} certs sent
                </span>
              )}
            </div>

            {/* Toolbar: drill level + search + view toggle + export */}
            <div className="reports__rotToolbar">
              <div className="reports__drillGroup">
                {([
                  { key: 'day'   as DrillLevel, label: 'Day',   disabled: false },
                  { key: 'week'  as DrillLevel, label: 'Week',  disabled: dateRange === 'today' },
                  { key: 'month' as DrillLevel, label: 'Month', disabled: dateRange === 'today' || dateRange === '7d' },
                ]).map(d => (
                  <button key={d.key}
                    className={`reports__drillBtn${rotDrill === d.key ? ' reports__drillBtn--active' : ''}${d.disabled ? ' reports__drillBtn--disabled' : ''}`}
                    onClick={() => !d.disabled && setRotDrill(d.key)}
                    disabled={d.disabled}>
                    {d.label}
                  </button>
                ))}
              </div>
              {rotView === 'table' && (
                <div className="reports__rotSearch">
                  <i className="pi pi-search reports__rotSearchIcon" />
                  <input
                    type="text"
                    className="reports__rotSearchInput"
                    placeholder="Search date…"
                    value={rotSearch}
                    onChange={e => setRotSearch(e.target.value)}
                  />
                  {rotSearch && (
                    <button className="reports__rotSearchClear" onClick={() => setRotSearch('')} title="Clear">
                      <i className="pi pi-times" />
                    </button>
                  )}
                </div>
              )}
              <div className="reports__rotActions">
                <div className="reports__viewToggle">
                  <button
                    className={`reports__viewBtn${rotView === 'chart' ? ' reports__viewBtn--active' : ''}`}
                    onClick={() => setRotView('chart')} title="Chart view">
                    <i className="pi pi-chart-line" />
                  </button>
                  <button
                    className={`reports__viewBtn${rotView === 'table' ? ' reports__viewBtn--active' : ''}`}
                    onClick={() => setRotView('table')} title="Table view">
                    <i className="pi pi-table" />
                  </button>
                </div>
                <button className="reports__exportBtn"
                  disabled={!rotData}
                  onClick={() => rotData && downloadRotCSV(rotData, `coi-volume-${TODAY_STR}.csv`)}
                  title="Export to CSV / Excel">
                  <i className="pi pi-file-excel" />
                  <span>Export</span>
                </button>
              </div>
            </div>

            {/* Summary strip — period statistics derived from daily data */}
            {rotSummary && !loading.requestsOverTime && (
              <div className="reports__rotSummary">
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">Today</span>
                  <span className="reports__rotSumValue">{rotSummary.todaySent.toLocaleString()}</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">This Week</span>
                  <span className="reports__rotSumValue">{rotSummary.weekSent.toLocaleString()}</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">This Month</span>
                  <span className="reports__rotSumValue">{rotSummary.monthSent.toLocaleString()}</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">Avg / Day</span>
                  <span className="reports__rotSumValue">{rotSummary.avgPerDay.toLocaleString()}</span>
                </div>
              </div>
            )}

            <div className="reports__cardBody">
              {loading.requestsOverTime ? <AreaSkeleton /> :
               errors.requestsOverTime  ? <CardError message={errors.requestsOverTime} /> :
               rotData
                 ? effectiveView(rotView) === 'chart'
                   ? rotSeries.length > 0
                     ? <Chart type="area" height="100%" series={rotSeries}
                         options={areaOpts(rotData.categories, rotSeries.map(s => s.data as number[]))} />
                     : <EmptyChart />
                   : (() => {
                     const showFailed  = hasData(rotData.failed);
                     const showPending = hasData(rotData.pending);
                     const colGroup = (
                       <colgroup>
                         <col className="col-date" />
                         <col className="col-num" />
                         {showFailed  && <col className="col-num" />}
                         {showPending && <col className="col-num" />}
                         <col className="col-num" />
                       </colgroup>
                     );
                     return (
                       <div className="reports__rotTableWrap">
                         {/* Fixed header */}
                         <div className="reports__rotTableHead">
                           <table>{colGroup}
                             <thead><tr>
                               <th>Date</th>
                               <th>Sent</th>
                               {showFailed  && <th>Failed</th>}
                               {showPending && <th>Pending</th>}
                               <th>Total</th>
                             </tr></thead>
                           </table>
                         </div>
                         {/* Scrollable body */}
                         <div className="reports__rotTableBody">
                           <table>{colGroup}
                             <tbody>
                               {(() => {
                                 const q = rotSearch.trim().toLowerCase();
                                 const filtered = rotData.categories
                                   .map((cat, i) => ({ cat, i }))
                                   .filter(({ cat }) => !q || cat.toLowerCase().includes(q));
                                 if (filtered.length === 0) {
                                   const colCount = 2 + (showFailed ? 1 : 0) + (showPending ? 1 : 0) + 1;
                                   return (
                                     <tr>
                                       <td colSpan={colCount} className="reports__rotNoResults">
                                         <i className="pi pi-search" /> No results for "{rotSearch}"
                                       </td>
                                     </tr>
                                   );
                                 }
                                 return filtered.map(({ cat, i }) => {
                                   const total = (rotData.sent[i] ?? 0)
                                     + (rotData.failed[i] ?? 0)
                                     + (rotData.pending[i] ?? 0);
                                   return (
                                     <tr key={i}>
                                       <td className="reports__rotTd--date">{cat}</td>
                                       <td className="reports__rotTd--num reports__rotTd--sent">{rotData.sent[i] ?? 0}</td>
                                       {showFailed  && <td className="reports__rotTd--num reports__rotTd--fail">{rotData.failed[i]  ?? 0}</td>}
                                       {showPending && <td className="reports__rotTd--num reports__rotTd--pend">{rotData.pending[i] ?? 0}</td>}
                                       <td className="reports__rotTd--num reports__rotTd--total">{total}</td>
                                     </tr>
                                   );
                                 });
                               })()}
                             </tbody>
                           </table>
                         </div>
                         {/* Fixed footer totals */}
                         <div className="reports__rotTableFoot">
                           <table>{colGroup}
                             <tbody><tr>
                               <td>Grand Total</td>
                               <td className="reports__rotTd--num reports__rotTd--sent">{rotData.sent.reduce((a,b)=>a+b,0)}</td>
                               {showFailed  && <td className="reports__rotTd--num reports__rotTd--fail">{rotData.failed.reduce((a,b)=>a+b,0)}</td>}
                               {showPending && <td className="reports__rotTd--num reports__rotTd--pend">{rotData.pending.reduce((a,b)=>a+b,0)}</td>}
                               <td className="reports__rotTd--num reports__rotTd--total">
                                 {rotData.sent.reduce((a,b)=>a+b,0)+rotData.failed.reduce((a,b)=>a+b,0)+rotData.pending.reduce((a,b)=>a+b,0)}
                               </td>
                             </tr></tbody>
                           </table>
                         </div>
                       </div>
                     );
                   })()
                 : <EmptyChart />}
            </div>
          </div>

          {/* Row 1 col 2 ── ACORD Form Types */}
          <div className="reports__card">
            <div className="reports__cardHeader">
              <i className="pi pi-file-edit reports__cardIcon" />
              <span className="reports__cardTitle">ACORD Form Types</span>
              {formApiData && !formLoading && (
                <span className="reports__cardBadge">
                  {formTypeSeries.values.reduce((a, b) => a + b, 0).toLocaleString()} certs
                </span>
              )}
            </div>

            {/* Toolbar: date range pills + search + view toggle + export */}
            <div className="reports__rotToolbar">
              {/* Date range filter */}
              <div className="reports__drillGroup">
                {([
                  { label: 'Today', value: 'today' as DateRange },
                  { label: '7 Days', value: '7d' as DateRange },
                  { label: '30 Days', value: '30d' as DateRange },
                ] as { label: string; value: DateRange }[]).map(f => (
                  <button key={f.value}
                    className={`reports__drillBtn${formDateRange === f.value ? ' reports__drillBtn--active' : ''}`}
                    onClick={() => setFormDateRange(f.value)}>
                    {f.label}
                  </button>
                ))}
              </div>
              {formView === 'table' && (
                <div className="reports__rotSearch">
                  <i className="pi pi-search reports__rotSearchIcon" />
                  <input
                    type="text"
                    className="reports__rotSearchInput"
                    placeholder="Search form…"
                    value={formSearch}
                    onChange={e => setFormSearch(e.target.value)}
                  />
                  {formSearch && (
                    <button className="reports__rotSearchClear" onClick={() => setFormSearch('')} title="Clear">
                      <i className="pi pi-times" />
                    </button>
                  )}
                </div>
              )}
              <div className="reports__rotActions">
                <div className="reports__viewToggle">
                  <button
                    className={`reports__viewBtn${formView === 'chart' ? ' reports__viewBtn--active' : ''}`}
                    onClick={() => setFormView('chart')} title="Chart view">
                    <i className="pi pi-chart-bar" />
                  </button>
                  <button
                    className={`reports__viewBtn${formView === 'table' ? ' reports__viewBtn--active' : ''}`}
                    onClick={() => setFormView('table')} title="Table view">
                    <i className="pi pi-table" />
                  </button>
                </div>
                <button className="reports__exportBtn"
                  disabled={!formApiData || !hasData(formTypeSeries.values)}
                  onClick={() => hasData(formTypeSeries.values) &&
                    downloadFormTypeCSV(formTypeSeries.categories, formTypeSeries.values, `acord-form-types-${TODAY_STR}.csv`)}
                  title="Export to CSV / Excel">
                  <i className="pi pi-file-excel" />
                  <span>Export</span>
                </button>
              </div>
            </div>

            {/* ACORD summary strip */}
            {formSummary && !formLoading && (
              <div className="reports__rotSummary">
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">Total Certs</span>
                  <span className="reports__rotSumValue">{formSummary.totalCerts.toLocaleString()}</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">Forms Used</span>
                  <span className="reports__rotSumValue">{formSummary.formsUsed}</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem reports__rotSumItem--wide">
                  <span className="reports__rotSumLabel">Top Form</span>
                  <span className="reports__rotSumValue reports__rotSumValue--sm">{formSummary.topForm}</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">ACORD 101</span>
                  <span className="reports__rotSumValue">{formSummary.acord101Count.toLocaleString()}</span>
                </div>
              </div>
            )}

            <div className="reports__cardBody">
              {formLoading ? <VBarSkeleton cols={8} /> :
               formError   ? <CardError message={formError} /> :
               formApiData && hasData(formTypeSeries.values)
                 ? effectiveView(formView) === 'chart'
                   ? <Chart type="bar" height="100%"
                       series={[{ name: 'Certificates', data: formTypeSeries.values }]}
                       options={formTypeColOpts(formTypeSeries.categories)} />
                   : (() => {
                     const q = formSearch.trim().toLowerCase();
                     const filtered = formTypeSeries.categories
                       .map((cat, i) => ({ cat, val: formTypeSeries.values[i] ?? 0, i }))
                       .filter(r => !q || r.cat.toLowerCase().includes(q));
                     const grandTotal = formTypeSeries.values.reduce((a, b) => a + b, 0);
                     return (
                       <div className="reports__rotTableWrap">
                         <div className="reports__rotTableHead">
                           <table>
                             <colgroup>
                               <col className="col-date" />
                               <col className="col-num" />
                               <col className="col-num" />
                             </colgroup>
                             <thead><tr>
                               <th>ACORD Form</th>
                               <th>Count</th>
                               <th>Share %</th>
                             </tr></thead>
                           </table>
                         </div>
                         <div className="reports__rotTableBody">
                           <table>
                             <colgroup>
                               <col className="col-date" />
                               <col className="col-num" />
                               <col className="col-num" />
                             </colgroup>
                             <tbody>
                               {filtered.length === 0
                                 ? <tr><td colSpan={3} className="reports__rotNoResults">
                                     <i className="pi pi-search" /> No results for "{formSearch}"
                                   </td></tr>
                                 : filtered.map(({ cat, val }) => (
                                   <tr key={cat}>
                                     <td className="reports__rotTd--date">{cat}</td>
                                     <td className="reports__rotTd--num reports__rotTd--sent">{val.toLocaleString()}</td>
                                     <td className="reports__rotTd--num reports__rotTd--total">
                                       {grandTotal > 0 ? `${((val / grandTotal) * 100).toFixed(1)}%` : '—'}
                                     </td>
                                   </tr>
                                 ))
                               }
                             </tbody>
                           </table>
                         </div>
                         <div className="reports__rotTableFoot">
                           <table>
                             <colgroup>
                               <col className="col-date" />
                               <col className="col-num" />
                               <col className="col-num" />
                             </colgroup>
                             <tbody><tr>
                               <td>Grand Total</td>
                               <td className="reports__rotTd--num reports__rotTd--sent">{grandTotal.toLocaleString()}</td>
                               <td className="reports__rotTd--num reports__rotTd--total">100%</td>
                             </tr></tbody>
                           </table>
                         </div>
                       </div>
                     );
                   })()
                 : <EmptyChart />}
            </div>
          </div>

          {/* Row 1 col 3 ── Peak Usage Hours */}
          <div className="reports__card">
            <div className="reports__cardHeader">
              <i className="pi pi-th-large reports__cardIcon" />
              <span className="reports__cardTitle">Peak Usage Hours</span>
              {data.peakUsage && !loading.peakUsage && peakSummary && (
                <span className="reports__cardBadge">{peakSummary.totalCerts.toLocaleString()} certs</span>
              )}
            </div>

            {/* Toolbar */}
            <div className="reports__rotToolbar">
              <div className="reports__drillGroup">
                {([
                  { key: 'all',      label: 'All Hours'  },
                  { key: 'business', label: 'Business'   },
                  { key: 'after',    label: 'After Hrs'  },
                ] as { key: 'all' | 'business' | 'after'; label: string }[]).map(f => (
                  <button key={f.key}
                    className={`reports__drillBtn${peakFilter === f.key ? ' reports__drillBtn--active' : ''}`}
                    onClick={() => setPeakFilter(f.key)}>
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="reports__rotActions">
                <div className="reports__viewToggle">
                  <button
                    className={`reports__viewBtn${peakView === 'chart' ? ' reports__viewBtn--active' : ''}`}
                    onClick={() => setPeakView('chart')} title="Heatmap view">
                    <i className="pi pi-th-large" />
                  </button>
                  <button
                    className={`reports__viewBtn${peakView === 'table' ? ' reports__viewBtn--active' : ''}`}
                    onClick={() => setPeakView('table')} title="Table view">
                    <i className="pi pi-table" />
                  </button>
                </div>
              </div>
            </div>

            {/* Summary strip */}
            {peakSummary && !loading.peakUsage && (
              <div className="reports__rotSummary">
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">Total Certs</span>
                  <span className="reports__rotSumValue">{peakSummary.totalCerts.toLocaleString()}</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem reports__rotSumItem--wide">
                  <span className="reports__rotSumLabel">Busiest Day</span>
                  <span className="reports__rotSumValue reports__rotSumValue--sm">{peakSummary.busiestDay}</span>
                  <span className="reports__rotSumSub">{peakSummary.busiestDayCount.toLocaleString()} certs</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">Peak Hour</span>
                  <span className="reports__rotSumValue reports__rotSumValue--sm">{peakSummary.busiestHour}</span>
                  <span className="reports__rotSumSub">{peakSummary.busiestHourCount.toLocaleString()} certs</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem reports__rotSumItem--wide">
                  <span className="reports__rotSumLabel">Peak Period</span>
                  <span className="reports__rotSumValue reports__rotSumValue--sm">{peakSummary.busiestPeriod}</span>
                  <span className="reports__rotSumSub">{peakSummary.busiestPeriodCnt.toLocaleString()} certs</span>
                </div>
              </div>
            )}

            <div className="reports__cardBody">
              {loading.peakUsage ? <HeatmapSkeleton /> :
               errors.peakUsage  ? <CardError message={errors.peakUsage} /> :
               data.peakUsage
                 ? peakView === 'chart'
                   ? <Chart type="heatmap" height="100%" series={heatmapSeries} options={heatmapOpts()} />
                   : (() => {
                     const hrs = peakFilter === 'business' ? PEAK_BIZ_HOURS
                               : peakFilter === 'after'    ? PEAK_AFTER_HOURS
                               : PEAK_ALL_HOURS;

                     // Period groups for sub-header (only for All Hours)
                     const PERIOD_GROUPS =
                       peakFilter === 'all'      ? [
                         { label: 'Night  12a–5a',     count: 6,  cls: 'peak-period-night'     },
                         { label: 'Morning  6a–11a',   count: 6,  cls: 'peak-period-morning'   },
                         { label: 'Afternoon  12p–5p', count: 6,  cls: 'peak-period-afternoon' },
                         { label: 'Evening  6p–11p',   count: 6,  cls: 'peak-period-evening'   },
                       ]
                       : peakFilter === 'business' ? [
                         { label: 'Business Hours  8a–5p', count: 10, cls: 'peak-period-morning' },
                       ]
                       : [
                         { label: 'Night  12a–7a',  count: 8, cls: 'peak-period-night'   },
                         { label: 'Evening  6p–11p', count: 6, cls: 'peak-period-evening' },
                       ];

                     // Which hours are at period boundaries (gets left border)
                     const boundarySet = new Set<number>();
                     let runIdx = 0;
                     PERIOD_GROUPS.forEach(g => { boundarySet.add(runIdx); runIdx += g.count; });

                     const shortHr = (h: string) => h.replace('am','a').replace('pm','p');

                     // Pre-build hour maps
                     const dayMaps = data.peakUsage!.days.map(day => {
                       const m: Record<string, number> = {};
                       day.data.forEach(h => { m[h.hour] = h.count; });
                       return { name: day.day, m };
                     });
                     const colTotals  = hrs.map(h => dayMaps.reduce((a, d) => a + (d.m[h] ?? 0), 0));
                     const grandTotal = colTotals.reduce((a, b) => a + b, 0);

                     return (
                       <div className="reports__peakTableWrap">
                         <table className="reports__peakTable">
                           <thead>
                             {/* Period group sub-header */}
                             <tr className="peak-group-row">
                               <th className="peak-col-day peak-group-label" rowSpan={2}>DAY</th>
                               {PERIOD_GROUPS.map(g => (
                                 <th key={g.label} colSpan={g.count} className={`peak-group-header ${g.cls}`}>
                                   {g.label}
                                 </th>
                               ))}
                               <th className="peak-col-total peak-group-label" rowSpan={2}>TOTAL</th>
                             </tr>
                             {/* Hour labels row */}
                             <tr>
                               {hrs.map((h, i) => (
                                 <th key={h}
                                   className={boundarySet.has(i) ? 'peak-period-boundary' : ''}>
                                   {shortHr(h)}
                                 </th>
                               ))}
                             </tr>
                           </thead>
                           <tbody>
                             {dayMaps.map(({ name, m }) => {
                               const rowTotal = hrs.reduce((a, h) => a + (m[h] ?? 0), 0);
                               return (
                                 <tr key={name}>
                                   <td className="peak-col-day">{name}</td>
                                   {hrs.map((h, i) => {
                                     const v   = m[h] ?? 0;
                                     const cls = boundarySet.has(i) ? 'peak-period-boundary' : '';
                                     return (
                                       <td key={h} className={cls}
                                         style={{ ...peakCellStyle(v), fontWeight: v > 0 ? 700 : 400 }}>
                                         {v > 0 ? v : ''}
                                       </td>
                                     );
                                   })}
                                   <td className="peak-col-total">{rowTotal > 0 ? rowTotal : '—'}</td>
                                 </tr>
                               );
                             })}
                           </tbody>
                           <tfoot>
                             <tr>
                               <td className="peak-col-day peak-foot">TOTAL</td>
                               {colTotals.map((t, i) => (
                                 <td key={i}
                                   className={`peak-foot${boundarySet.has(i) ? ' peak-period-boundary' : ''}`}>
                                   {t > 0 ? t : ''}
                                 </td>
                               ))}
                               <td className="peak-col-total peak-foot">{grandTotal}</td>
                             </tr>
                           </tfoot>
                         </table>
                       </div>
                     );
                   })()
                 : <EmptyChart />}
            </div>
          </div>

          {/* ── Row 2 flex wrapper ── */}
          <div className="reports__r2Wrap">

          {/* Row 2 col 1 ── Top Insureds */}
          <div className="reports__card">
            <div className="reports__cardHeader">
              <i className="pi pi-building reports__cardIcon" />
              <span className="reports__cardTitle">Top Insureds</span>
              {topSummary && !topLoading && (
                <span className="reports__cardBadge">{topSummary.totalCOIs.toLocaleString()} COIs</span>
              )}
            </div>

            {/* Toolbar */}
            <div className="reports__rotToolbar">
              {/* Date range */}
              <div className="reports__drillGroup">
                {([
                  { label: 'Today',   value: 'today' as DateRange },
                  { label: '7 Days',  value: '7d'    as DateRange },
                  { label: '30 Days', value: '30d'   as DateRange },
                ] as { label: string; value: DateRange }[]).map(f => (
                  <button key={f.value}
                    className={`reports__drillBtn${topDateRange === f.value ? ' reports__drillBtn--active' : ''}`}
                    onClick={() => setTopDateRange(f.value)}>
                    {f.label}
                  </button>
                ))}
              </div>
              {/* Top N drill selector — dropdown */}
              <select
                className="reports__topLimitSelect"
                value={topLimit}
                onChange={e => setTopLimit(Number(e.target.value) as 5 | 10 | 20)}>
                <option value={5}>Top 5</option>
                <option value={10}>Top 10</option>
                <option value={20}>Top 20</option>
              </select>
              {/* Search — visible in both modes */}
              <div className="reports__rotSearch">
                <i className="pi pi-search reports__rotSearchIcon" />
                <input
                  type="text"
                  className="reports__rotSearchInput"
                  placeholder="Search insured…"
                  value={topSearch}
                  onChange={e => setTopSearch(e.target.value)}
                />
                {topSearch && (
                  <button className="reports__rotSearchClear" onClick={() => setTopSearch('')} title="Clear">
                    <i className="pi pi-times" />
                  </button>
                )}
              </div>
              <div className="reports__rotActions">
                <div className="reports__viewToggle">
                  <button
                    className={`reports__viewBtn${topView === 'chart' ? ' reports__viewBtn--active' : ''}`}
                    onClick={() => setTopView('chart')} title="Chart view">
                    <i className="pi pi-chart-bar" />
                  </button>
                  <button
                    className={`reports__viewBtn${topView === 'table' ? ' reports__viewBtn--active' : ''}`}
                    onClick={() => setTopView('table')} title="Table view">
                    <i className="pi pi-table" />
                  </button>
                </div>
                <button className="reports__exportBtn"
                  disabled={!topSummary || topSummary.totalCOIs === 0}
                  onClick={() => topSummary && topSummary.agents.length > 0 &&
                    downloadTopInsuredsCSV(topSummary.agents, topSummary.totalCOIs,
                      `top-insureds-${topDateRange}-${TODAY_STR}.csv`)}
                  title="Export to CSV">
                  <i className="pi pi-file-excel" />
                  <span>Export</span>
                </button>
              </div>
            </div>

            {/* Summary strip */}
            {topSummary && !topLoading && (
              <div className="reports__rotSummary">
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">Total COIs</span>
                  <span className="reports__rotSumValue">{topSummary.totalCOIs.toLocaleString()}</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">Insureds</span>
                  <span className="reports__rotSumValue">{topSummary.insuredCount}</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem reports__rotSumItem--wide">
                  <span className="reports__rotSumLabel">Top Insured</span>
                  <span className="reports__rotSumValue reports__rotSumValue--sm">{topSummary.topName}</span>
                  <span className="reports__rotSumSub">{topSummary.topCount.toLocaleString()} COIs · {topSummary.topShare}% share</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">Avg / Insured</span>
                  <span className="reports__rotSumValue">{topSummary.avgPerInsured.toLocaleString()}</span>
                </div>
              </div>
            )}

            <div className="reports__cardBody">
              {topLoading ? <TopInsuredsSkeleton rows={topLimit} /> :
               topError   ? <CardError message={topError} /> :
               topSummary && topSummary.agents.length > 0
                 ? effectiveView(topView) === 'chart'
                   ? (() => {
                     const agents = topSummary.agents;
                     const maxV   = agents[0]?.count ?? 1;

                     // Rank-based color palette: warm→cool as rank increases
                     const BAR_COLORS = [
                       '#f59e0b','#f97316','#3b82f6','#8b5cf6','#14b8a6',
                       '#22c55e','#ec4899','#6366f1','#06b6d4','#84cc16',
                       '#ef4444','#a855f7','#0ea5e9','#10b981','#f43f5e',
                       '#eab308','#7c3aed','#059669','#d946ef','#64748b',
                     ];

                     // Nice x-axis ticks: 5 evenly spaced from 0 to maxV
                     const rawStep = maxV / 4;
                     const mag = Math.pow(10, Math.floor(Math.log10(rawStep || 1)));
                     const niceStep = Math.ceil(rawStep / mag) * mag;
                     const ticks: number[] = [];
                     for (let t = 0; t <= maxV + niceStep * 0.5; t += niceStep) ticks.push(t);

                     // Spacer = rankW + nameW + gaps + rowPad
                     // .top-bar-row: pl-6, rank 24px, gap 6px, name 148px, gap 8px = 192px total
                     const SPACER = 192;

                     return (
                       <div className="reports__topBarsWrap">
                         {/* Scrollable bars */}
                         <div className="reports__topBarsScroll">
                           {agents.map((a, i) => {
                             const pct   = (a.count / maxV) * 100;
                             const color = BAR_COLORS[i % BAR_COLORS.length];
                             const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null;
                             const showInside = pct > 22;
                             return (
                               <div key={a.name}
                                 className={`top-bar-row${i === 0 ? ' top-bar-row--first' : ''}`}>
                                 <div className="top-bar-rank">
                                   {medal
                                     ? <span className="top-medal">{medal}</span>
                                     : <span className="top-rank-num">{i + 1}</span>}
                                 </div>
                                 <div className="top-bar-name" title={a.name}>{a.name}</div>
                                 <div className="top-bar-track-wrap">
                                   {/* Tick grid lines */}
                                   {ticks.map(t => t > 0 && (
                                     <div key={t} className="top-tick-line"
                                       style={{ left: `${(t / maxV) * 100}%` }} />
                                   ))}
                                   {/* Bar fill */}
                                   <div className="top-bar-fill-custom"
                                     style={{
                                       width: `${pct}%`,
                                       background: `linear-gradient(90deg, ${color}cc 0%, ${color} 100%)`,
                                     }}>
                                     {showInside && (
                                       <span className="top-bar-count top-bar-count--in">
                                         {a.count.toLocaleString()}
                                       </span>
                                     )}
                                   </div>
                                   {!showInside && (
                                     <span className="top-bar-count top-bar-count--out"
                                       style={{ left: `calc(${pct}% + 5px)`, color }}>
                                       {a.count.toLocaleString()}
                                     </span>
                                   )}
                                 </div>
                               </div>
                             );
                           })}
                         </div>
                         {/* Fixed x-axis */}
                         <div className="reports__topXAxis">
                           <div className="top-xaxis-spacer" style={{ width: SPACER }} />
                           <div className="reports__topXTicks">
                             {ticks.map(t => (
                               <span key={t} className="top-xtick"
                                 style={{ left: `${(t / maxV) * 100}%` }}>
                                 {t >= 1000 ? `${(t / 1000).toFixed(1)}k` : t}
                               </span>
                             ))}
                           </div>
                         </div>
                       </div>
                     );
                   })()
                   : (() => {
                     const agents = topSummary.agents;
                     const total  = topSummary.totalCOIs;
                     const max    = agents[0]?.count ?? 1;
                     return (
                       <div className="reports__topTableWrap">
                         <table className="reports__topTable">
                           <thead>
                             <tr>
                               <th className="top-col-rank">#</th>
                               <th className="top-col-name">Insured Name</th>
                               <th className="top-col-bar">Volume</th>
                               <th className="top-col-count">COIs</th>
                               <th className="top-col-share">Share</th>
                             </tr>
                           </thead>
                           <tbody>
                             {agents.map((a, i) => {
                               const pct   = total > 0 ? (a.count / total * 100) : 0;
                               const barW  = max  > 0 ? (a.count / max  * 100) : 0;
                               const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null;
                               return (
                                 <tr key={a.name} className={i === 0 ? 'top-row--first' : ''}>
                                   <td className="top-col-rank">
                                     {medal
                                       ? <span className="top-medal">{medal}</span>
                                       : <span className="top-rank-num">{i + 1}</span>}
                                   </td>
                                   <td className="top-col-name" title={a.name}>{a.name}</td>
                                   <td className="top-col-bar">
                                     <div className="top-bar-track">
                                       <div className="top-bar-fill" style={{ width: `${barW}%` }} />
                                     </div>
                                   </td>
                                   <td className="top-col-count">{a.count.toLocaleString()}</td>
                                   <td className="top-col-share">{pct.toFixed(1)}%</td>
                                 </tr>
                               );
                             })}
                           </tbody>
                           <tfoot>
                             <tr className="top-foot">
                               <td colSpan={3} className="top-col-foot-label">TOTAL ({agents.length} insureds)</td>
                               <td className="top-col-count">{total.toLocaleString()}</td>
                               <td className="top-col-share">100%</td>
                             </tr>
                           </tfoot>
                         </table>
                         {topSearch && agents.length === 0 && (
                           <div className="reports__rotNoResults">
                             <i className="pi pi-search" /> No results for "{topSearch}"
                           </div>
                         )}
                       </div>
                     );
                   })()
                 : topSearch
                   ? <div className="reports__rotNoResults">
                       <i className="pi pi-search" /> No results for "{topSearch}"
                     </div>
                   : <EmptyChart />}
            </div>
          </div>

          {/* Row 2 col 2 ── Certificates by Line of Business */}
          <div className="reports__card">
            <div className="reports__cardHeader">
              <i className="pi pi-chart-bar reports__cardIcon" />
              <span className="reports__cardTitle">Certificates by Line of Business</span>
              {lobSummary && !lobLoading && (
                <span className="reports__cardBadge">{lobSummary.total.toLocaleString()} certs</span>
              )}
            </div>

            {/* Toolbar */}
            <div className="reports__rotToolbar">
              <div className="reports__drillGroup">
                {([
                  { label: 'Today',   value: 'today' as DateRange },
                  { label: '7 Days',  value: '7d'    as DateRange },
                  { label: '30 Days', value: '30d'   as DateRange },
                ] as { label: string; value: DateRange }[]).map(f => (
                  <button key={f.value}
                    className={`reports__drillBtn${lobDateRange === f.value ? ' reports__drillBtn--active' : ''}`}
                    onClick={() => setLobDateRange(f.value)}>
                    {f.label}
                  </button>
                ))}
              </div>
              {lobView === 'table' && (
                <div className="reports__rotSearch">
                  <i className="pi pi-search reports__rotSearchIcon" />
                  <input type="text" className="reports__rotSearchInput"
                    placeholder="Search LOB…" value={lobSearch}
                    onChange={e => setLobSearch(e.target.value)} />
                  {lobSearch && (
                    <button className="reports__rotSearchClear" onClick={() => setLobSearch('')}>
                      <i className="pi pi-times" />
                    </button>
                  )}
                </div>
              )}
              <div className="reports__rotActions">
                <div className="reports__viewToggle">
                  <button
                    className={`reports__viewBtn${lobView === 'chart' ? ' reports__viewBtn--active' : ''}`}
                    onClick={() => setLobView('chart')} title="Chart view">
                    <i className="pi pi-chart-bar" />
                  </button>
                  <button
                    className={`reports__viewBtn${lobView === 'table' ? ' reports__viewBtn--active' : ''}`}
                    onClick={() => setLobView('table')} title="Table view">
                    <i className="pi pi-table" />
                  </button>
                </div>
                <button className="reports__exportBtn"
                  disabled={!lobSummary || lobSummary.total === 0}
                  onClick={() => lobSummary && lobFiltered.length > 0 &&
                    downloadLobCSV(lobFiltered, lobSummary.total,
                      `lob-breakdown-${lobDateRange}-${TODAY_STR}.csv`)}
                  title="Export CSV">
                  <i className="pi pi-file-excel" />
                  <span>Export</span>
                </button>
              </div>
            </div>

            {/* Summary strip */}
            {lobSummary && !lobLoading && (
              <div className="reports__rotSummary">
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">Total Certs</span>
                  <span className="reports__rotSumValue">{lobSummary.total.toLocaleString()}</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">LOBs</span>
                  <span className="reports__rotSumValue">{lobSummary.lobCount}</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem reports__rotSumItem--wide">
                  <span className="reports__rotSumLabel">Top LOB</span>
                  <span className="reports__rotSumValue reports__rotSumValue--sm">{lobSummary.topLob}</span>
                  <span className="reports__rotSumSub">{lobSummary.topCount.toLocaleString()} certs · {lobSummary.topShare}%</span>
                </div>
              </div>
            )}

            <div className="reports__cardBody">
              {lobLoading ? <TreemapSkeleton /> :
               lobError   ? <CardError message={lobError} /> :
               lobSummary && lobSummary.total > 0
                 ? effectiveView(lobView) === 'chart'
                   ? (() => {
                     const maxV  = lobFiltered[0]?.count ?? 1;
                     const total = lobSummary.total;
                     // nice x-axis ticks
                     const rawStep  = maxV / 4;
                     const mag      = Math.pow(10, Math.floor(Math.log10(rawStep || 1)));
                     const niceStep = Math.ceil(rawStep / mag) * mag;
                     const ticks: number[] = [];
                     for (let t = 0; t <= maxV + niceStep * 0.5; t += niceStep) ticks.push(t);
                     const NAME_W = 148;
                     return (
                       <div className="lob__lollipopWrap">
                         {/* scrollable rows */}
                         <div className="lob__lollipopScroll">
                           {lobFiltered.map((l: LobItem, i: number) => {
                             const pct  = (l.count / maxV) * 100;
                             const clr  = LOB_COLORS[i % LOB_COLORS.length];
                             const share = total > 0 ? (l.count / total * 100).toFixed(1) : '0';
                             // dot radius 6–14 px scaled by count
                             const dotR = Math.round(6 + Math.sqrt(l.count / maxV) * 8);
                             return (
                               <div key={l.lob} className="lob__lolRow">
                                 <span className="lob__lolName" title={l.lob}>{l.lob}</span>
                                 <div className="lob__lolTrack">
                                   {/* grid tick lines */}
                                   {ticks.map(t => t > 0 && (
                                     <div key={t} className="lob__lolTick"
                                       style={{ left: `${(t / maxV) * 100}%` }} />
                                   ))}
                                   {/* stem line */}
                                   <div className="lob__lolStem"
                                     style={{ width: `calc(${pct}% - ${dotR}px)`, background: `${clr}55` }} />
                                   {/* dot */}
                                   <div className="lob__lolDot"
                                     style={{
                                       left: `${pct}%`,
                                       width: dotR * 2, height: dotR * 2,
                                       marginLeft: -(dotR * 2),
                                       background: clr,
                                       boxShadow: `0 0 0 3px ${clr}33`,
                                     }}>
                                     <span className="lob__lolDotLabel">{l.count}</span>
                                   </div>
                                   {/* share badge */}
                                   <span className="lob__lolShare"
                                     style={{ left: `calc(${pct}% + ${dotR + 6}px)`, color: clr }}>
                                     {share}%
                                   </span>
                                 </div>
                               </div>
                             );
                           })}
                         </div>
                         {/* fixed x-axis */}
                         <div className="lob__lolXAxis">
                           <div style={{ width: NAME_W, flexShrink: 0 }} />
                           <div className="lob__lolXTicks">
                             {ticks.map(t => (
                               <span key={t} className="lob__lolXTick"
                                 style={{ left: `${(t / maxV) * 100}%` }}>
                                 {t >= 1000 ? `${(t / 1000).toFixed(1)}k` : t}
                               </span>
                             ))}
                           </div>
                         </div>
                       </div>
                     );
                   })()
                   : (() => {
                     const total = lobSummary.total;
                     const max   = lobFiltered[0]?.count ?? 1;
                     return (
                       <div className="reports__topTableWrap">
                         <table className="reports__topTable">
                           <thead>
                             <tr>
                               <th className="top-col-rank">#</th>
                               <th className="top-col-name">Line of Business</th>
                               <th className="top-col-bar">Volume</th>
                               <th className="top-col-count">Certs</th>
                               <th className="top-col-share">Share</th>
                             </tr>
                           </thead>
                           <tbody>
                             {lobFiltered.map((l: LobItem, i: number) => {
                               const pct  = total > 0 ? (l.count / total * 100) : 0;
                               const barW = max  > 0 ? (l.count / max  * 100) : 0;
                               const clr  = LOB_COLORS[i % LOB_COLORS.length];
                               return (
                                 <tr key={l.lob} className={i === 0 ? 'top-row--first' : ''}>
                                   <td className="top-col-rank">
                                     <span className="top-rank-num" style={{ background: `${clr}22`, color: clr }}>
                                       {i + 1}
                                     </span>
                                   </td>
                                   <td className="top-col-name" title={l.lob}>{l.lob}</td>
                                   <td className="top-col-bar">
                                     <div className="top-bar-track">
                                       <div className="top-bar-fill"
                                         style={{ width: `${barW}%`,
                                           background: `linear-gradient(90deg,${clr}99,${clr})` }} />
                                     </div>
                                   </td>
                                   <td className="top-col-count" style={{ color: clr }}>{l.count.toLocaleString()}</td>
                                   <td className="top-col-share">{pct.toFixed(1)}%</td>
                                 </tr>
                               );
                             })}
                           </tbody>
                           <tfoot>
                             <tr className="top-foot">
                               <td colSpan={3} className="top-col-foot-label">
                                 TOTAL ({lobFiltered.length} LOBs)
                               </td>
                               <td className="top-col-count">{total.toLocaleString()}</td>
                               <td className="top-col-share">100%</td>
                             </tr>
                           </tfoot>
                         </table>
                         {lobSearch && lobFiltered.length === 0 && (
                           <div className="reports__rotNoResults">
                             <i className="pi pi-search" /> No results for "{lobSearch}"
                           </div>
                         )}
                       </div>
                     );
                   })()
                 : <EmptyChart />}
            </div>
          </div>

          {/* Row 2 col 3 ── Agent vs Internal Usage */}
          <div className="reports__card">
            <div className="reports__cardHeader">
              <i className="pi pi-users reports__cardIcon" />
              <span className="reports__cardTitle">Agent vs Employee activity</span>
              {agentApiData && !agentLoading && (
                <span className="reports__cardBadge">{agentApiData.total.toLocaleString()} total</span>
              )}
            </div>

            {/* Toolbar: date filter + cert type toggle + view toggle + export */}
            <div className="reports__rotToolbar">
              <div className="reports__drillGroup">
                {(['today','7d','30d'] as DateRange[]).map(r => (
                  <button key={r}
                    className={`reports__drillBtn${agentDateRange === r ? ' reports__drillBtn--active' : ''}`}
                    onClick={() => setAgentDateRange(r)}>
                    {r === 'today' ? 'Today' : r === '7d' ? '7 Days' : '30 Days'}
                  </button>
                ))}
              </div>
              {/* Cert type flip toggle */}
              <div className="agent__certToggle">
                <button
                  className={`agent__certBtn${agentCertType === 'sent' ? ' agent__certBtn--active' : ''}`}
                  onClick={() => setAgentCertType('sent')}>
                  <i className="pi pi-send" /> Sent COIs
                </button>
                <button
                  className={`agent__certBtn${agentCertType === 'master' ? ' agent__certBtn--active' : ''}`}
                  onClick={() => setAgentCertType('master')}>
                  <i className="pi pi-file" /> Master Certs
                </button>
              </div>
              <div className="reports__rotActions">
                <div className="reports__viewToggle">
                  <button
                    className={`reports__viewBtn${agentView === 'chart' ? ' reports__viewBtn--active' : ''}`}
                    onClick={() => setAgentView('chart')} title="Pie chart">
                    <i className="pi pi-chart-pie" />
                  </button>
                  <button
                    className={`reports__viewBtn${agentView === 'table' ? ' reports__viewBtn--active' : ''}`}
                    onClick={() => setAgentView('table')} title="Table view">
                    <i className="pi pi-table" />
                  </button>
                </div>
                <button className="reports__exportBtn"
                  onClick={downloadAgentCSV}
                  disabled={!agentApiData || agentLoading}>
                  <i className="pi pi-file-export" /> Export
                </button>
              </div>
            </div>

            {/* Summary strip */}
            {agentApiData && !agentLoading && agentApiData.total > 0 && (
              <div className="reports__rotSummary">
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">Total Certs</span>
                  <span className="reports__rotSumValue">{agentApiData.total.toLocaleString()}</span>
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">Agent</span>
                  <span className="reports__rotSumValue agent__sumValue--agent">{agentApiData.agent.toLocaleString()}</span>
                  <span className="reports__rotSumSub">{agentApiData.agentPct}% share</span>
                  {agentApiData.agentChange !== 0 && (
                    <span className={agentApiData.agentChange >= 0 ? 'agent__trend--up' : 'agent__trend--down'}>
                      {agentApiData.agentChange >= 0 ? '▲' : '▼'} {Math.abs(agentApiData.agentChange).toFixed(1)}%
                    </span>
                  )}
                </div>
                <div className="reports__rotSumDivider" />
                <div className="reports__rotSumItem">
                  <span className="reports__rotSumLabel">Employee</span>
                  <span className="reports__rotSumValue agent__sumValue--employee">{agentApiData.employee.toLocaleString()}</span>
                  <span className="reports__rotSumSub">{agentApiData.employeePct}% share</span>
                  {agentApiData.employeeChange !== 0 && (
                    <span className={agentApiData.employeeChange >= 0 ? 'agent__trend--up' : 'agent__trend--down'}>
                      {agentApiData.employeeChange >= 0 ? '▲' : '▼'} {Math.abs(agentApiData.employeeChange).toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="reports__cardBody">
              {agentLoading ? <DonutSkeleton /> :
               agentError   ? <CardError message={agentError} /> :
               agentApiData && agentApiData.total > 0
                 ? effectiveView(agentView) === 'chart'
                   ? (() => {
                     const AGENT_COLORS = ['#3b82f6', '#f97316', '#8b5cf6', '#22c55e'];
                     const series = agentApiData.rows.map((r: UserTypeBreakdownRow) => r.count);
                     const labels = agentApiData.rows.map((r: UserTypeBreakdownRow) => r.userType);
                     const colors = labels.map((l, i) => l === 'Agent' ? '#3b82f6' : l === 'Employee' ? '#f97316' : AGENT_COLORS[i]);
                     return (
                       <Chart type="donut" height="100%"
                         series={series}
                         options={{
                           chart: { type: 'donut', toolbar: { show: false }, animations: { enabled: true, speed: 500 } },
                           labels,
                           colors,
                           plotOptions: {
                             pie: {
                               donut: {
                                 size: '55%',
                                 labels: {
                                   show: true,
                                   name:  { fontSize: '11px', fontWeight: '700', color: '#334155' },
                                   value: { fontSize: '16px', fontWeight: '800', color: '#0057A4',
                                     formatter: (v: string) => Number(v).toLocaleString() },
                                   total: {
                                     show: true,
                                     label: agentCertType === 'sent' ? 'Sent Certs' : 'Master Certs',
                                     fontSize: '9px', fontWeight: '700', color: '#94a3b8',
                                     formatter: (w: { globals: { seriesTotals: number[] } }) =>
                                       w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0).toLocaleString(),
                                   },
                                 },
                               },
                             },
                           },
                           dataLabels: {
                             enabled: true,
                             formatter: (val: number) => val >= 5 ? `${val.toFixed(1)}%` : '',
                             style: { fontSize: '11px', fontWeight: '700', colors: ['#fff'] },
                             dropShadow: { enabled: false },
                           },
                           stroke: { width: 3, colors: ['#fff'] },
                           legend: {
                             show: true, position: 'bottom',
                             fontSize: '11px', fontWeight: '700',
                             markers: { size: 8 },
                             formatter: (label: string, opts: { w: { globals: { series: number[]; seriesTotals: number[] } }; seriesIndex: number }) => {
                               const total = opts.w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0);
                               const val   = opts.w.globals.series[opts.seriesIndex] ?? 0;
                               const pct   = total > 0 ? ((val / total) * 100).toFixed(1) : '0';
                               return `${label} — ${val.toLocaleString()} (${pct}%)`;
                             },
                           },
                           tooltip: { theme: 'light', y: { formatter: (v: number) => `${v.toLocaleString()} certs` } },
                           grid: { padding: { top: 0, right: 0, bottom: 0, left: 0 } },
                         }}
                       />
                     );
                   })()
                   : (() => {
                     const total = agentApiData.total;
                     const max   = Math.max(...agentApiData.rows.map((r: UserTypeBreakdownRow) => r.count), 1);
                     const TYPE_COLORS: Record<string, string> = { Agent: '#3b82f6', Employee: '#f97316' };
                     return (
                       <div className="reports__topTableWrap">
                         <table className="reports__topTable">
                           <thead>
                             <tr>
                               <th className="top-col-rank">#</th>
                               <th className="top-col-name">User Type</th>
                               <th className="top-col-bar">Volume</th>
                               <th className="top-col-count">Certs</th>
                               <th className="top-col-share">Share</th>
                               <th className="top-col-share">vs Prior</th>
                             </tr>
                           </thead>
                           <tbody>
                             {agentApiData.rows.map((r: UserTypeBreakdownRow, i: number) => {
                               const clr  = TYPE_COLORS[r.userType] ?? '#6366f1';
                               const barW = max > 0 ? (r.count / max * 100) : 0;
                               const change = r.userType === 'Agent' ? agentApiData.agentChange : agentApiData.employeeChange;
                               return (
                                 <tr key={r.userType} className={i === 0 ? 'top-row--first' : ''}>
                                   <td className="top-col-rank">
                                     <span className="top-rank-num" style={{ background: `${clr}22`, color: clr }}>{i + 1}</span>
                                   </td>
                                   <td className="top-col-name">
                                     <span className="agent__typeTag" style={{ background: `${clr}18`, color: clr, borderColor: `${clr}44` }}>
                                       <i className={`pi ${r.userType === 'Agent' ? 'pi-user' : 'pi-building'}`} />
                                       {r.userType}
                                     </span>
                                   </td>
                                   <td className="top-col-bar">
                                     <div className="top-bar-track">
                                       <div className="top-bar-fill"
                                         style={{ width: `${barW}%`, background: `linear-gradient(90deg,${clr}99,${clr})` }} />
                                     </div>
                                   </td>
                                   <td className="top-col-count" style={{ color: clr }}>{r.count.toLocaleString()}</td>
                                   <td className="top-col-share">{r.sharePct.toFixed(1)}%</td>
                                   <td className="top-col-share">
                                     {change !== 0
                                       ? <span className={change >= 0 ? 'agent__trendUp' : 'agent__trendDown'}>
                                           {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(1)}%
                                         </span>
                                       : <span className="agent__trendNeutral">—</span>}
                                   </td>
                                 </tr>
                               );
                             })}
                           </tbody>
                           <tfoot>
                             <tr className="top-foot">
                               <td colSpan={3} className="top-col-foot-label">TOTAL</td>
                               <td className="top-col-count">{total.toLocaleString()}</td>
                               <td className="top-col-share">100%</td>
                               <td />
                             </tr>
                           </tfoot>
                         </table>
                       </div>
                     );
                   })()
                 : <EmptyChart />}
            </div>
          </div>

          {/* Row 2 col 4 ── Error Reports (collapsible) */}
          <div ref={errCardRef}
            className={`reports__card reports__errCard${errCollapsed ? ' reports__errCard--collapsed' : ' reports__errCard--expanded'}`}>

            {/* ── COLLAPSED VIEW ── */}
            {errCollapsed && (
              <div className="reports__errCollapsed">

                {/* Header */}
                <div className="reports__errCollapsedHdr">
                  <i className="pi pi-exclamation-triangle" style={{ fontSize: 9, color: '#ef4444' }} />
                  <span className="reports__errCollapsedHdrTitle">Error Reports</span>
                  <button className="reports__errToggleBtn" onClick={() => setErrCollapsed(false)} title="Expand Error Reports">
                    <i className="pi pi-chevron-left" />
                  </button>
                </div>

                {/* 3 mini KPI cards */}
                <div className="reports__errMiniCards">
                  {([
                    { label: 'Login Error',     icon: 'pi-lock',       value: errSummary?.loginErrors     ?? 0, color: '#ef4444' },
                    { label: 'Send Certificate',icon: 'pi-send',       value: errSummary?.sendCertErrors   ?? 0, color: '#f97316' },
                    { label: 'Master Cert',     icon: 'pi-file',       value: errSummary?.masterCertErrors ?? 0, color: '#a855f7' },
                  ]).map(k => (
                    <div key={k.label} className="reports__errMiniCard"
                      onClick={() => setErrCollapsed(false)}
                      title={`${k.label} errors — click to expand`}>
                      <i className={`pi ${k.icon} reports__errMiniCard__icon`} style={{ color: k.color }} />
                      <span className="reports__errMiniCard__label" style={{ color: k.color }}>{k.label}</span>
                      <span className="reports__errMiniCard__value" style={{ color: k.value > 0 ? k.color : '#cbd5e1' }}>
                        {errLoading ? '…' : k.value}
                      </span>
                    </div>
                  ))}
                </div>

              </div>
            )}

            {/* ── EXPANDED VIEW ── */}
            {!errCollapsed && (
              <>
                {/* Header */}
                <div className="reports__cardHeader">
                  <i className="pi pi-exclamation-triangle reports__cardIcon" style={{ color: '#ef4444' }} />
                  <span className="reports__cardTitle">Error Reports</span>
                  {errSummary && !errLoading && (
                    <span className="reports__cardBadge" style={{ background: '#fef2f2', color: '#ef4444' }}>
                      {errSummary.total.toLocaleString()} errors
                    </span>
                  )}
                  {/* Collapse button */}
                  <button className="reports__errToggleBtn" style={{ marginLeft: 'auto' }} onClick={() => setErrCollapsed(true)} title="Collapse Error Reports">
                    <i className="pi pi-chevron-right" />
                  </button>
                </div>

                {/* Toolbar */}
                <div className="reports__rotToolbar">
                  <div className="reports__drillGroup">
                    {([
                      { label: 'Today',   value: 'today' as DateRange },
                      { label: '7 Days',  value: '7d'    as DateRange },
                      { label: '30 Days', value: '30d'   as DateRange },
                    ] as { label: string; value: DateRange }[]).map(f => (
                      <button key={f.value}
                        className={`reports__drillBtn${errDateRange === f.value ? ' reports__drillBtn--active' : ''}`}
                        onClick={() => setErrDateRange(f.value)}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <div className="reports__rotActions">
                    <div className="reports__viewToggle">
                      <button
                        className={`reports__viewBtn${effectiveView(errView) === 'chart' ? ' reports__viewBtn--active' : ''}`}
                        onClick={() => setErrView('chart')} title="Chart view">
                        <i className="pi pi-chart-bar" />
                      </button>
                      <button
                        className={`reports__viewBtn${effectiveView(errView) === 'table' ? ' reports__viewBtn--active' : ''}`}
                        onClick={() => setErrView('table')} title="Table view">
                        <i className="pi pi-table" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* KPI summary strip — 5 items in one row */}
                {errKpis && !errLoading && (
                  <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
                    {errKpis.map((k, i) => (
                      <React.Fragment key={k.label}>
                        {i > 0 && <div style={{ width: 1, background: '#f1f5f9', flexShrink: 0 }} />}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, padding: '5px 6px' }}>
                          <span style={{ fontSize: 8, fontWeight: 700, color: k.color, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{k.label}</span>
                          <span style={{ fontSize: 15, fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>{k.value.toLocaleString()}</span>
                          {k.change !== 0 && (
                            <span style={{ fontSize: 8, fontWeight: 600, color: k.change < 0 ? '#16a34a' : '#ef4444' }}>
                              {k.change > 0 ? '▲' : '▼'} {Math.abs(k.change).toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                )}

                {/* Chart / Table body */}
                <div className="reports__cardBody" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {errLoading ? <VBarSkeleton cols={8} /> :
                   errError   ? <CardError message={errError} /> :
                   errApiData
                     ? effectiveView(errView) === 'chart'
                       ? errSeries.length > 0
                         ? <Chart type="bar" height="100%" series={errSeries}
                             options={errorBarOpts(errApiData.categories)} />
                         : <EmptyChart label="No errors in this period" />
                       : (
                         /* ── Table view ── */
                         <div className="reports__errTableWrap">
                           {errApiData.recentErrors.length === 0
                             ? <div className="reports__errTableEmpty">
                                 <i className="pi pi-check-circle" style={{ color: '#16a34a' }} />
                                 No errors in this period
                               </div>
                             : <table className="reports__errTable">
                                 <thead>
                                   <tr>
                                     <th style={{ width: '18%' }}>Time</th>
                                     <th style={{ width: '12%' }}>Type</th>
                                     <th style={{ width: '22%' }}>Method</th>
                                     <th>Error Message</th>
                                   </tr>
                                 </thead>
                                 <tbody>
                                   {errApiData.recentErrors.map((r, i) => {
                                     const meta = ERROR_TYPE_LABELS[r.errorType] ?? { label: r.errorType, color: '#94a3b8' };
                                     return (
                                       <tr key={i}>
                                         <td className="reports__errTable__time">
                                           {new Date(r.createdAt).toLocaleString()}
                                         </td>
                                         <td>
                                           <span className="reports__errTypeBadge" style={{ background: `${meta.color}18`, color: meta.color }}>
                                             {meta.label}
                                           </span>
                                         </td>
                                         <td className="reports__errTable__method" title={r.methodName ?? ''}>
                                           {r.methodName ?? '—'}
                                         </td>
                                         <td className="reports__errTable__msg" title={r.errorMessage ?? ''}>
                                           {r.errorMessage || '—'}
                                         </td>
                                       </tr>
                                     );
                                   })}
                                 </tbody>
                               </table>
                           }
                         </div>
                       )
                     : <EmptyChart label="No errors in this period" />}
                </div>
              </>
            )}

          </div>

          {/* ── Close Row 2 flex wrapper ── */}
          </div>

        </div>
        </div>
      )}
    </div>
  );
}
